import d3 from 'd3';
import Emitter from 'component-emitter';
import { forEach } from 'lodash-es';
import SphericalMercator from 'sphericalmercator';
import PriorityQueue from 'priorityqueuejs';
import SVG from 'svg.js';
import roundedRect from 'rounded-rect';
import measureText from 'measure-text';

/**
 * General Transitive utilities library
 */
const TOLERANCE = 0.000001;

function fuzzyEquals(a, b, tolerance = TOLERANCE) {
  return Math.abs(a - b) < tolerance;
}

function distance(x1, y1, x2, y2) {
  return Math.sqrt((x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1));
}

function getRadiusFromAngleChord(angleR, chordLen) {
  return chordLen / 2 / Math.sin(angleR / 2);
}
/*
 * CCW utility function. Accepts 3 coord pairs; result is positive if points
 * have counterclockwise orientation, negative if clockwise, 0 if collinear.
 */


function ccw(ax, ay, bx, by, cx, cy) {
  const raw = ccwRaw(ax, ay, bx, by, cx, cy);
  return raw === 0 ? 0 : raw / Math.abs(raw);
}

function ccwRaw(ax, ay, bx, by, cx, cy) {
  return (bx - ax) * (cy - ay) - (cx - ax) * (by - ay);
}
/*
 * Compute angle formed by three points in cartesian plane using law of cosines
 */


function angleFromThreePoints(ax, ay, bx, by, cx, cy) {
  const c = distance(ax, ay, bx, by);
  const a = distance(bx, by, cx, cy);
  const b = distance(ax, ay, cx, cy);
  return Math.acos((a * a + c * c - b * b) / (2 * a * c));
}

function pointAlongArc(x1, y1, x2, y2, r, theta, ccw, t) {
  ccw = Math.abs(ccw) / ccw; // convert to 1 or -1

  let rot = Math.PI / 2 - Math.abs(theta) / 2;
  const vectToCenter = normalizeVector(rotateVector({
    x: x2 - x1,
    y: y2 - y1
  }, ccw * rot)); // calculate the center of the arc circle

  const cx = x1 + r * vectToCenter.x;
  const cy = y1 + r * vectToCenter.y;
  let vectFromCenter = negateVector(vectToCenter);
  rot = Math.abs(theta) * t * ccw;
  vectFromCenter = normalizeVector(rotateVector(vectFromCenter, rot));
  return {
    x: cx + r * vectFromCenter.x,
    y: cy + r * vectFromCenter.y
  };
}

function getVectorAngle(x, y) {
  let t = Math.atan(y / x);
  if (x < 0 && t <= 0) t += Math.PI;else if (x < 0 && t >= 0) t -= Math.PI;
  return t;
}

function rayIntersection(ax, ay, avx, avy, bx, by, bvx, bvy) {
  const u = ((by - ay) * bvx - (bx - ax) * bvy) / (bvx * avy - bvy * avx);
  const v = ((by - ay) * avx - (bx - ax) * avy) / (bvx * avy - bvy * avx);
  return {
    intersect: u > -TOLERANCE && v > -TOLERANCE,
    u: u,
    v: v
  };
}

function lineIntersection(x1, y1, x2, y2, x3, y3, x4, y4) {
  const d = (x1 - x2) * (y3 - y4) - (y1 - y2) * (x3 - x4);

  if (d === 0) {
    // lines are parallel
    return {
      intersect: false
    };
  }

  return {
    intersect: true,
    x: ((x1 * y2 - y1 * x2) * (x3 - x4) - (x1 - x2) * (x3 * y4 - y3 * x4)) / d,
    y: ((x1 * y2 - y1 * x2) * (y3 - y4) - (y1 - y2) * (x3 * y4 - y3 * x4)) / d
  };
}
/**
 * Whether vector is projected into positive xy quadrant.
 */


function isOutwardVector(vector) {
  return !fuzzyEquals(vector.x, 0) ? vector.x > 0 : vector.y > 0;
}
/**
 * vector utilities
 */


function normalizeVector(v) {
  const d = Math.sqrt(v.x * v.x + v.y * v.y);
  return {
    x: v.x / d,
    y: v.y / d
  };
}

function rotateVector(v, theta) {
  return {
    x: v.x * Math.cos(theta) - v.y * Math.sin(theta),
    y: v.x * Math.sin(theta) + v.y * Math.cos(theta)
  };
}

function negateVector(v) {
  return {
    x: -v.x,
    y: -v.y
  };
}
/**
 * GTFS utilities
 */


function otpModeToGtfsType(otpMode) {
  switch (otpMode) {
    case 'TRAM':
      return 0;

    case 'SUBWAY':
      return 1;

    case 'RAIL':
      return 2;

    case 'BUS':
      return 3;

    case 'FERRY':
      return 4;

    case 'CABLE_CAR':
      return 5;

    case 'GONDOLA':
      return 6;

    case 'FUNICULAR':
      return 7;
  }
} // Rendering utilities


function renderDataToSvgPath(renderData) {
  return renderData.map((d, k) => {
    if (k === 0) return `M${d.x} ${d.y}`;

    if (d.arc) {
      return `A${d.radius} ${d.radius} ${d.arc} 0 ${d.arc > 0 ? 0 : 1} ${d.x} ${d.y}`;
    }

    return `L${d.x} ${d.y}`;
  }).join(' ');
} // An instance of the SphericalMercator converter


const sm = /*#__PURE__*/new SphericalMercator();
/**
 * @param {*} fontSize A CSS font size or a numerical (pixel) font size.
 * @returns A CSS font size ending with the provided CSS unit or 'px' if none provided.
 */

function getFontSizeWithUnit(fontSize) {
  return fontSize + (isFinite(fontSize) ? 'px' : '');
}

/**
 * Label object
 */
class Label {
  constructor(parent) {
    this.parent = parent;
    this.sortableType = 'LABEL';
  }

  getText() {
    if (!this.labelText) this.labelText = this.initText();
    return this.labelText;
  }

  initText() {
    // TODO: determine why getName is missing for patterns running on routes
    // without short names
    return typeof this.parent.getName === 'function' ? this.parent.getName() : null;
  }

  render(display) {
    throw new Error('method not defined by subclass!');
  }
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  refresh(display) {}

  setVisibility(visibility) {
    if (this.svgGroup) {
      this.svgGroup.attr('display', visibility ? 'initial' : 'none');
    }
  }

  getBBox() {
    return null;
  }

  intersects(obj) {
    return null;
  }

  intersectsBBox(bbox) {
    const thisBBox = this.getBBox(this.orientation);
    const r = thisBBox.x <= bbox.x + bbox.width && bbox.x <= thisBBox.x + thisBBox.width && thisBBox.y <= bbox.y + bbox.height && bbox.y <= thisBBox.y + thisBBox.height;
    return r;
  }

  isFocused() {
    return this.parent.isFocused();
  }

  getZIndex() {
    return 1000000;
  }

}

/**
 * Label object
 */

class PointLabel extends Label {
  constructor(parent) {
    super(parent);
    this.labelAngle = 0;
    this.labelPosition = 1;
  }

  initText() {
    return this.parent.getName();
  }
  /* render (display) {
    this.svgGroup = display.svg.append('g') // this.parent.labelSvg;
    this.svgGroup
      .attr('class', 'transitive-sortable')
      .datum({
        owner: this,
        sortableType: 'POINT_LABEL'
      })
     var typeStr = this.parent.getType().toLowerCase()
     this.mainLabel = this.svgGroup.append('text')
      .datum({
        owner: this
      })
      .attr('id', 'transitive-' + typeStr + '-label-' + this.parent.getId())
      .text(this.getText())
      .attr('font-size', this.fontSize)
      .attr('font-family', this.fontFamily)
      .attr('class', 'transitive-' + typeStr + '-label')
  }
   refresh (display) {
    if (!this.labelAnchor) return
     if (!this.svgGroup) this.render(display)
     this.svgGroup
      .attr('text-anchor', this.labelPosition > 0 ? 'start' : 'end')
      .attr('transform', (d, i) => {
        return 'translate(' + this.labelAnchor.x + ',' + this.labelAnchor
          .y + ')'
      })
     this.mainLabel
      .attr('transform', (d, i) => {
        return 'rotate(' + this.labelAngle + ', 0, 0)'
      })
  } */


  render(display) {
    const text = this.getText();
    if (!text || !this.labelAnchor) return;
    const anchor = {
      x: this.labelAnchor.x,
      y: this.labelAnchor.y - this.textHeight / 2
    }; // define common style attributes for the halo and main text

    const attrs = {
      fill: '#000',
      'font-family': display.styler.compute2('labels', 'font-family', this.parent),
      'font-size': getFontSizeWithUnit(this.fontSize),
      'text-anchor': this.labelPosition > 0 ? 'start' : 'end'
    }; // draw the halo

    display.drawText(text, anchor, Object.assign({}, attrs, {
      fill: 'none',
      stroke: '#fff',
      'stroke-opacity': 0.75,
      'stroke-width': 3
    })); // draw the main text

    display.drawText(text, anchor, attrs);
  }

  setOrientation(orientation) {
    this.orientation = orientation;
    const markerBBox = this.parent.getMarkerBBox();
    if (!markerBBox) return;
    let x, y;
    const offset = 5;

    if (orientation === 'E') {
      x = markerBBox.x + markerBBox.width + offset;
      y = markerBBox.y + markerBBox.height / 2;
      this.labelPosition = 1;
      this.labelAngle = 0;
    } else if (orientation === 'W') {
      x = markerBBox.x - offset;
      y = markerBBox.y + markerBBox.height / 2;
      this.labelPosition = -1;
      this.labelAngle = 0;
    } else if (orientation === 'NE') {
      x = markerBBox.x + markerBBox.width + offset;
      y = markerBBox.y - offset;
      this.labelPosition = 1;
      this.labelAngle = -45;
    } else if (orientation === 'SE') {
      x = markerBBox.x + markerBBox.width + offset;
      y = markerBBox.y + markerBBox.height + offset;
      this.labelPosition = 1;
      this.labelAngle = 45;
    } else if (orientation === 'NW') {
      x = markerBBox.x - offset;
      y = markerBBox.y - offset;
      this.labelPosition = -1;
      this.labelAngle = 45;
    } else if (orientation === 'SW') {
      x = markerBBox.x - offset;
      y = markerBBox.y + markerBBox.height + offset;
      this.labelPosition = -1;
      this.labelAngle = -45;
    } else if (orientation === 'N') {
      x = markerBBox.x + markerBBox.width / 2;
      y = markerBBox.y - offset;
      this.labelPosition = 1;
      this.labelAngle = -90;
    } else if (orientation === 'S') {
      x = markerBBox.x + markerBBox.width / 2;
      y = markerBBox.y + markerBBox.height + offset;
      this.labelPosition = -1;
      this.labelAngle = -90;
    }

    this.labelAnchor = {
      x: x,
      y: y
    };
  }

  getBBox() {
    if (this.orientation === 'E') {
      return {
        height: this.textHeight,
        width: this.textWidth,
        x: this.labelAnchor.x,
        y: this.labelAnchor.y - this.textHeight
      };
    }

    if (this.orientation === 'W') {
      return {
        height: this.textHeight,
        width: this.textWidth,
        x: this.labelAnchor.x - this.textWidth,
        y: this.labelAnchor.y - this.textHeight
      };
    }

    if (this.orientation === 'N') {
      return {
        height: this.textWidth,
        width: this.textHeight,
        x: this.labelAnchor.x - this.textHeight,
        y: this.labelAnchor.y - this.textWidth
      };
    }

    if (this.orientation === 'S') {
      return {
        height: this.textWidth,
        width: this.textHeight,
        x: this.labelAnchor.x - this.textHeight,
        y: this.labelAnchor.y
      };
    }

    const bboxSide = this.textWidth * Math.sqrt(2) / 2;

    if (this.orientation === 'NE') {
      return {
        height: bboxSide,
        width: bboxSide,
        x: this.labelAnchor.x,
        y: this.labelAnchor.y - bboxSide
      };
    }

    if (this.orientation === 'SE') {
      return {
        height: bboxSide,
        width: bboxSide,
        x: this.labelAnchor.x,
        y: this.labelAnchor.y
      };
    }

    if (this.orientation === 'NW') {
      return {
        height: bboxSide,
        width: bboxSide,
        x: this.labelAnchor.x - bboxSide,
        y: this.labelAnchor.y - bboxSide
      };
    }

    if (this.orientation === 'SW') {
      return {
        height: bboxSide,
        width: bboxSide,
        x: this.labelAnchor.x - bboxSide,
        y: this.labelAnchor.y
      };
    }
  }

  intersects(obj) {
    if (obj instanceof Label) {
      // todo: handle label-label intersection for diagonally placed labels separately
      return this.intersectsBBox(obj.getBBox());
    } else if (obj.x && obj.y && obj.width && obj.height) {
      return this.intersectsBBox(obj);
    }

    return false;
  }

  runFocusTransition(display, callback) {
    if (this.mainLabel) {
      if (this.parent.isFocused()) this.setVisibility(true);
      this.mainLabel.transition().style('opacity', this.parent.isFocused() ? 1 : 0).call(callback);
    }
  }

}

class Point {
  constructor(data) {
    for (const key in data) {
      this[key] = data[key];
    }

    this.paths = [];
    this.renderData = [];
    this.label = new PointLabel(this);
    this.renderLabel = true;
    this.focused = true;
    this.sortableType = 'POINT';
    this.placeOffsets = {
      x: 0,
      y: 0
    };
    this.zIndex = 10000;
  }
  /**
   * Get unique ID for point -- must be defined by subclass
   */


  getId() {
    throw new Error('method not defined by subclass!');
  }

  getElementId() {
    return this.getType().toLowerCase() + '-' + this.getId();
  }
  /**
   * Get Point type -- must be defined by subclass
   */


  getType() {
    throw new Error('method not defined by subclass!');
  }
  /**
   * Get Point name
   */


  getName() {
    return `${this.getType()} point (ID=${this.getId()})`;
  }
  /**
   * Get latitude
   */


  getLat() {
    return 0;
  }
  /**
   * Get longitude
   */


  getLon() {
    return 0;
  }

  containsSegmentEndPoint() {
    return false;
  }

  containsBoardPoint() {
    return false;
  }

  containsAlightPoint() {
    return false;
  }

  containsTransferPoint() {
    return false;
  }

  getPatterns() {
    return [];
  }
  /**
   * Draw the point
   *
   * @param {Display} display
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  render(display) {}
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  addRenderData() {}
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  clearRenderData() {}

  containsFromPoint() {
    return false;
  }

  containsToPoint() {
    return false;
  } //* * Shared geom utility functions **//


  constructMergedMarker(display) {
    const dataArray = this.getRenderDataArray();
    const xValues = [];
    const yValues = [];
    dataArray.forEach(function (data) {
      const x = data.x;
      const y = data.y;
      xValues.push(x);
      yValues.push(y);
    });
    const minX = Math.min.apply(Math, xValues);
    const minY = Math.min.apply(Math, yValues);
    const maxX = Math.max.apply(Math, xValues);
    const maxY = Math.max.apply(Math, yValues); // retrieve marker type and radius from the styler

    const markerType = display.styler.compute(display.styler.stops_merged['marker-type'], display, {
      owner: this
    });
    const stylerRadius = display.styler.compute(display.styler.stops_merged.r, display, {
      owner: this
    });
    let width;
    let height;
    let r; // if this is a circle marker w/ a styler-defined fixed radius, use that

    if (markerType === 'circle' && stylerRadius) {
      width = height = stylerRadius * 2;
      r = stylerRadius; // otherwise, this is a dynamically-sized marker
    } else {
      const dx = maxX - minX;
      const dy = maxY - minY;
      const markerPadding = display.styler.compute(display.styler.stops_merged['marker-padding'], display, {
        owner: this
      }) || 0;
      const patternRadius = display.styler.compute(display.styler[this.patternStylerKey].r, display, {
        owner: this
      });
      r = parseFloat(patternRadius) + markerPadding;

      if (markerType === 'circle') {
        width = height = Math.max(dx, dy) + 2 * r;
        r = width / 2;
      } else {
        width = dx + 2 * r;
        height = dy + 2 * r;
        if (markerType === 'rectangle') r = 0;
      }
    }

    return {
      height: height,
      rx: r,
      ry: r,
      width: width,
      x: (minX + maxX) / 2 - width / 2,
      y: (minY + maxY) / 2 - height / 2
    };
  }

  initMarkerData(display) {
    if (this.getType() !== 'STOP' && this.getType() !== 'MULTI') return;
    this.mergedMarkerData = this.constructMergedMarker(display);
    this.placeOffsets = {
      x: 0,
      y: 0
    };

    if (this.adjacentPlace) {
      const placeR = display.styler.compute(display.styler.places.r, display, {
        owner: this.adjacentPlace
      });
      const placeX = display.xScale.compute(this.adjacentPlace.worldX);
      const placeY = display.yScale.compute(this.adjacentPlace.worldY);
      const thisR = this.mergedMarkerData.width / 2;
      const thisX = this.mergedMarkerData.x + thisR;
      const thisY = this.mergedMarkerData.y + thisR;
      const dx = thisX - placeX;
      const dy = thisY - placeY;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (placeR + thisR > dist) {
        const f = (placeR + thisR) / dist;
        this.placeOffsets = {
          x: dx * f - dx,
          y: dy * f - dy
        };
        this.mergedMarkerData.x += this.placeOffsets.x;
        this.mergedMarkerData.y += this.placeOffsets.y;
        forEach(this.graphVertex.incidentEdges(), edge => {
          forEach(edge.renderSegments, segment => {
            segment.refreshRenderData(display);
          });
        });
      }
    }
  }

  getMarkerBBox() {
    return this.markerBBox;
  }

  setFocused(focused) {
    this.focused = focused;
  }

  isFocused() {
    return this.focused === true;
  }
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  runFocusTransition(display, callback) {}
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  setAllPatternsFocused() {}

  getZIndex() {
    return this.zIndex;
  }

  getAverageCoord() {
    const dataArray = this.getRenderDataArray();
    let xTotal = 0;
    let yTotal = 0;
    forEach(dataArray, data => {
      xTotal += data.x;
      yTotal += data.y;
    });
    return {
      x: xTotal / dataArray.length,
      y: yTotal / dataArray.length
    };
  }

  hasRenderData() {
    const dataArray = this.getRenderDataArray();
    return dataArray && dataArray.length > 0;
  }
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  makeDraggable(transitive) {}

  toString() {
    return `${this.getType()} point: ${this.getId()} (${this.getName()})`;
  }

}

/**
 *  Place: a Point subclass representing a 'place' that can be rendered on the
 *  map. A place is a point *other* than a transit stop/station, e.g. a home/work
 *  location, a point of interest, etc.
 */

class Stop extends Point {
  constructor(data) {
    super(data);

    if (data && data.stop_lat && data.stop_lon) {
      const xy = sm.forward([data.stop_lon, data.stop_lat]);
      this.worldX = xy[0];
      this.worldY = xy[1];
    }

    this.patterns = [];
    this.patternRenderData = {};
    this.patternFocused = {};
    this.patternCount = 0;
    this.patternStylerKey = 'stops_pattern';
    this.isSegmentEndPoint = false;
  }
  /**
   * Get id
   */


  getId() {
    return this.stop_id;
  }
  /**
   * Get type
   */


  getType() {
    return 'STOP';
  }
  /**
   * Get name
   */


  getName() {
    if (!this.stop_name) return `Unnamed Stop (ID=${this.getId()})`;
    return this.stop_name;
  }
  /**
   * Get lat
   */


  getLat() {
    return this.stop_lat;
  }
  /**
   * Get lon
   */


  getLon() {
    return this.stop_lon;
  }

  containsSegmentEndPoint() {
    return this.isSegmentEndPoint;
  }

  containsBoardPoint() {
    return this.isBoardPoint;
  }

  containsAlightPoint() {
    return this.isAlightPoint;
  }

  containsTransferPoint() {
    return this.isTransferPoint;
  }

  getPatterns() {
    return this.patterns;
  }

  addPattern(pattern) {
    if (this.patterns.indexOf(pattern) === -1) this.patterns.push(pattern);
  }
  /**
   * Add render data
   *
   * @param {Object} stopInfo
   */


  addRenderData(stopInfo) {
    if (stopInfo.rEdge.getType() === 'TRANSIT') {
      const s = {
        getZIndex: function () {
          if (this.owner.graphVertex) {
            return this.owner.getZIndex();
          }

          return this.rEdge.getZIndex() + 1;
        },
        owner: this,
        sortableType: 'POINT_STOP_PATTERN'
      };

      for (const key in stopInfo) s[key] = stopInfo[key];

      const patternId = stopInfo.rEdge.patternIds;
      this.patternRenderData[patternId] = s; // .push(s);

      forEach(stopInfo.rEdge.patterns, pattern => {
        this.addPattern(pattern);
      });
    }

    this.patternCount = Object.keys(this.patternRenderData).length;
  }

  isPatternFocused(patternId) {
    if (!(patternId in this.patternFocused)) return true;
    return this.patternFocused[patternId];
  }

  setPatternFocused(patternId, focused) {
    this.patternFocused[patternId] = focused;
  }

  setAllPatternsFocused(focused) {
    for (const key in this.patternRenderData) {
      this.patternFocused[key] = focused;
    }
  }
  /**
   * Draw a stop
   *
   * @param {Display} display
   */


  render(display) {
    super.render(display);
    if (this.patternCount === 0) return;
    this.initMarkerData(display);
    const styler = display.styler; // For segment endpoints, draw the "merged" marker

    if (this.isSegmentEndPoint && this.mergedMarkerData) {
      display.drawRect({
        x: this.mergedMarkerData.x,
        y: this.mergedMarkerData.y
      }, {
        fill: styler.compute2('stops_merged', 'fill', this),
        height: this.mergedMarkerData.height,
        rx: this.mergedMarkerData.rx,
        ry: this.mergedMarkerData.ry,
        stroke: styler.compute2('stops_merged', 'stroke', this),
        'stroke-width': styler.compute2('stops_merged', 'stroke-width', this),
        width: this.mergedMarkerData.width
      }); // store marker bounding box

      this.markerBBox = {
        height: this.mergedMarkerData.height,
        width: this.mergedMarkerData.width,
        x: this.mergedMarkerData.x,
        y: this.mergedMarkerData.y
      };
    } // TODO: Restore inline stop
  }

  getRenderDataArray() {
    const dataArray = [];

    for (const patternId in this.patternRenderData) {
      dataArray.push(this.patternRenderData[patternId]);
    }

    return dataArray;
  }

  clearRenderData() {
    this.patternRenderData = {};
    this.mergedMarkerData = null;
    this.placeOffsets = {
      x: 0,
      y: 0
    };
  }

}

/**
 *  Place: a Point subclass representing a 'place' that can be rendered on the
 *  map. A place is a point *other* than a transit stop/station, e.g. a home/work
 *  location, a point of interest, etc.
 */

class Place extends Point {
  /**
   *  the constructor
   */
  constructor(data) {
    super(data);

    if (data && data.place_lat && data.place_lon) {
      const xy = sm.forward([data.place_lon, data.place_lat]);
      this.worldX = xy[0];
      this.worldY = xy[1];
    }

    this.zIndex = 100000;
  }
  /**
   * Get Type
   */


  getType() {
    return 'PLACE';
  }
  /**
   * Get ID
   */


  getId() {
    return this.place_id;
  }
  /**
   * Get Name
   */


  getName() {
    return this.place_name;
  }
  /**
   * Get lat
   */


  getLat() {
    return this.place_lat;
  }
  /**
   * Get lon
   */


  getLon() {
    return this.place_lon;
  }

  containsSegmentEndPoint() {
    return true;
  }

  containsFromPoint() {
    return this.getId() === 'from';
  }

  containsToPoint() {
    return this.getId() === 'to';
  }

  addRenderData(pointInfo) {
    this.renderData.push(pointInfo);
  }

  getRenderDataArray() {
    return this.renderData;
  }

  clearRenderData() {
    this.renderData = [];
  }
  /**
   * Draw a place
   *
   * @param {Display} display
   */


  render(display) {
    super.render(display);
    const styler = display.styler;
    if (!this.renderData) return;
    const displayStyle = styler.compute2('places', 'display', this);
    if (displayStyle === 'none') return;
    this.renderXY = {
      x: display.xScale.compute(display.activeZoomFactors.useGeographicRendering ? this.worldX : this.graphVertex.x),
      y: display.yScale.compute(display.activeZoomFactors.useGeographicRendering ? this.worldY : this.graphVertex.y)
    };
    const radius = styler.compute2('places', 'r', this) || 10;
    display.drawCircle(this.renderXY, {
      fill: styler.compute2('places', 'fill', this) || '#fff',
      r: radius,
      stroke: styler.compute2('places', 'stroke', this) || '#000',
      'stroke-width': styler.compute2('places', 'stroke-width', this) || 2
    });
    this.markerBBox = {
      height: radius * 2,
      width: radius * 2,
      x: this.renderXY.x - radius,
      y: this.renderXY.y - radius
    };
  }

}

/**
 * Utility class used when clustering points into MultiPoint objects
 */

class PointCluster {
  constructor() {
    this.points = [];
  }

  addPoint(point) {
    if (this.points.indexOf(point) === -1) this.points.push(point);
  }

  mergeVertices(graph) {
    const vertices = [];
    forEach(this.points, point => {
      vertices.push(point.graphVertex);
    });
    graph.mergeVertices(vertices);
  }

}

/**
 *  MultiPoint: a Point subclass representing a collection of multiple points
 *  that have been merged into one for display purposes.
 */

class MultiPoint extends Point {
  constructor(pointArray) {
    super();
    this.points = [];

    if (pointArray) {
      forEach(pointArray, point => {
        this.addPoint(point);
      });
    }

    this.renderData = [];
    this.id = 'multi';
    this.toPoint = this.fromPoint = null;
    this.patternStylerKey = 'multipoints_pattern';
  }
  /**
   * Get id
   */


  getId() {
    return this.id;
  }
  /**
   * Get type
   */


  getType() {
    return 'MULTI';
  }

  getName() {
    if (this.fromPoint) return this.fromPoint.getName();
    if (this.toPoint) return this.toPoint.getName();
    let shortest = null;
    forEach(this.points, point => {
      if (point.getType() === 'TURN') return;

      if (!shortest || point.getName().length < shortest.length) {
        shortest = point.getName();
      }
    });
    return shortest;
  }

  containsSegmentEndPoint() {
    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].containsSegmentEndPoint()) return true;
    }

    return false;
  }

  containsBoardPoint() {
    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].containsBoardPoint()) return true;
    }

    return false;
  }

  containsAlightPoint() {
    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].containsAlightPoint()) return true;
    }

    return false;
  }

  containsTransferPoint() {
    for (let i = 0; i < this.points.length; i++) {
      if (this.points[i].containsTransferPoint()) return true;
    }

    return false;
  }

  containsFromPoint() {
    return this.fromPoint !== null;
  }

  containsToPoint() {
    return this.toPoint !== null;
  }

  getPatterns() {
    const patterns = [];
    forEach(this.points, point => {
      if (!point.patterns) return;
      forEach(point.patterns, pattern => {
        if (patterns.indexOf(pattern) === -1) patterns.push(pattern);
      });
    });
    return patterns;
  }

  addPoint(point) {
    if (this.points.indexOf(point) !== -1) return;
    this.points.push(point);
    this.id += '-' + point.getId();

    if (point.containsFromPoint()) {
      // getType() === 'PLACE' && point.getId() === 'from') {
      this.fromPoint = point;
    }

    if (point.containsToPoint()) {
      // getType() === 'PLACE' && point.getId() === 'to') {
      this.toPoint = point;
    }

    this.calcWorldCoords();
  }

  calcWorldCoords() {
    let tx = 0;
    let ty = 0;
    forEach(this.points, point => {
      tx += point.worldX;
      ty += point.worldY;
    });
    this.worldX = tx / this.points.length;
    this.worldY = ty / this.points.length;
  }
  /**
   * Add render data
   *
   * @param {Object} stopInfo
   */


  addRenderData(pointInfo) {
    if (pointInfo.offsetX !== 0 || pointInfo.offsetY !== 0) {
      this.hasOffsetPoints = true;
    }

    this.renderData.push(pointInfo);
  }

  clearRenderData() {
    this.hasOffsetPoints = false;
    this.renderData = [];
  }
  /**
   * Draw a multipoint
   *
   * @param {Display} display
   */


  render(display) {
    super.render(display);
    if (!this.renderData) return; // Compute the bounds of the merged marker

    const xArr = this.renderData.map(d => d.x);
    const yArr = this.renderData.map(d => d.y);
    const xMin = Math.min(...xArr);
    const xMax = Math.max(...xArr);
    const yMin = Math.min(...yArr);
    const yMax = Math.max(...yArr);
    const r = 6;
    const x = xMin - r;
    const y = yMin - r;
    const width = xMax - xMin + r * 2;
    const height = yMax - yMin + r * 2; // Draw the merged marker

    display.drawRect({
      x,
      y
    }, {
      fill: '#fff',
      height,
      rx: r,
      ry: r,
      stroke: '#000',
      'stroke-width': 2,
      width
    }); // Store marker bounding box

    this.markerBBox = {
      height,
      width,
      x,
      y
    }; // TODO: support pattern-specific markers
  }

  initMergedMarker(display) {
    // set up the merged marker
    if (this.fromPoint || this.toPoint) {
      this.mergedMarker = this.markerSvg.append('g').append('circle').datum({
        owner: this
      }).attr('class', 'transitive-multipoint-marker-merged');
    } else if (this.hasOffsetPoints || this.renderData.length > 1) {
      this.mergedMarker = this.markerSvg.append('g').append('rect').datum({
        owner: this
      }).attr('class', 'transitive-multipoint-marker-merged');
    }
  }

  getRenderDataArray() {
    return this.renderData;
  }

  setFocused(focused) {
    this.focused = focused;
    forEach(this.points, point => {
      point.setFocused(focused);
    });
  }

  runFocusTransition(display, callback) {
    if (this.mergedMarker) {
      const newStrokeColor = display.styler.compute(display.styler.multipoints_merged.stroke, display, {
        owner: this
      });
      this.mergedMarker.transition().style('stroke', newStrokeColor).call(callback);
    }

    if (this.label) this.label.runFocusTransition(display, callback);
  }

}

/**
 * Utility class to cluster points into MultiPoint objects
 */

class PointClusterMap {
  constructor(transitive) {
    this.transitive = transitive;
    this.clusters = [];
    this.clusterLookup = {}; // maps Point object to its containing cluster

    const pointArr = [];
    forEach(Object.values(transitive.stops), point => {
      if (point.used) pointArr.push(point);
    }, this);
    forEach(Object.values(transitive.turnPoints), turnPoint => {
      pointArr.push(turnPoint);
    }, this);
    const links = d3.geom.voronoi().x(function (d) {
      return d.worldX;
    }).y(function (d) {
      return d.worldY;
    }).links(pointArr);
    forEach(links, link => {
      const dist = distance(link.source.worldX, link.source.worldY, link.target.worldX, link.target.worldY);

      if (dist < 100 && (link.source.getType() !== 'TURN' || link.target.getType() !== 'TURN')) {
        const sourceInCluster = (link.source in this.clusterLookup);
        const targetInCluster = (link.target in this.clusterLookup);

        if (sourceInCluster && !targetInCluster) {
          this.addPointToCluster(link.target, this.clusterLookup[link.source]);
        } else if (!sourceInCluster && targetInCluster) {
          this.addPointToCluster(link.source, this.clusterLookup[link.target]);
        } else if (!sourceInCluster && !targetInCluster) {
          const cluster = new PointCluster();
          this.clusters.push(cluster);
          this.addPointToCluster(link.source, cluster);
          this.addPointToCluster(link.target, cluster);
        }
      }
    }, this);
    this.vertexPoints = [];
    forEach(this.clusters, cluster => {
      const multipoint = new MultiPoint(cluster.points);
      this.vertexPoints.push(multipoint);
      forEach(cluster.points, point => {
        point.multipoint = multipoint;
      });
    });
  }

  addPointToCluster(point, cluster) {
    cluster.addPoint(point);
    this.clusterLookup[point] = cluster;
  }

  clearMultiPoints() {
    forEach(this.clusters, cluster => {
      forEach(cluster.points, point => {
        point.multipoint = null;
      });
    });
  }

  getVertexPoints(baseVertexPoints) {
    if (!baseVertexPoints) return this.vertexPoints;
    const vertexPoints = this.vertexPoints.concat();
    forEach(baseVertexPoints, point => {
      if (!point.multipoint) vertexPoints.push(point);
    });
    return vertexPoints;
  }

}

let rEdgeId = 0;
/**
 * RenderedEdge
 */

class RenderedEdge {
  constructor(graphEdge, forward, type, useGeographicRendering) {
    this.id = rEdgeId++;
    this.graphEdge = graphEdge;
    this.forward = forward;
    this.type = type;
    this.points = [];
    this.clearOffsets();
    this.focused = true;
    this.sortableType = 'SEGMENT';
    this.useGeographicRendering = useGeographicRendering;
  }

  clearGraphData() {
    this.graphEdge = null;
    this.edgeFromOffset = 0;
    this.edgeToOffset = 0;
  }

  addPattern(pattern) {
    if (!this.patterns) this.patterns = [];
    if (this.patterns.indexOf(pattern) !== -1) return;
    this.patterns.push(pattern); // generate the patternIds field

    this.patternIds = constuctIdListString(this.patterns);
  }

  addPathSegment(pathSegment) {
    if (!this.pathSegments) this.pathSegments = [];
    if (this.pathSegments.indexOf(pathSegment) !== -1) return;
    this.pathSegments.push(pathSegment); // generate the pathSegmentIds field

    this.pathSegmentIds = constuctIdListString(this.pathSegments);
  }

  getId() {
    return this.id;
  }

  getType() {
    return this.type;
  }

  setFromOffset(offset) {
    this.fromOffset = offset;
  }

  setToOffset(offset) {
    this.toOffset = offset;
  }

  clearOffsets() {
    this.fromOffset = 0;
    this.toOffset = 0;
  }

  getAlignmentVector(alignmentId) {
    if (this.graphEdge.getFromAlignmentId() === alignmentId) {
      return this.graphEdge.fromVector;
    }

    if (this.graphEdge.getToAlignmentId() === alignmentId) {
      return this.graphEdge.toVector;
    }

    return null;
  }

  offsetAlignment(alignmentId, offset) {
    // If from/to alignment IDs match, set respective offset.
    if (this.graphEdge.getFromAlignmentId() === alignmentId) {
      this.setFromOffset(isOutwardVector(this.graphEdge.fromVector) ? offset : -offset);
    }

    if (this.graphEdge.getToAlignmentId() === alignmentId) {
      this.setToOffset(isOutwardVector(this.graphEdge.toVector) ? offset : -offset);
    }
  }

  setFocused(focused) {
    this.focused = focused;
  }

  refreshRenderData(display) {
    if (this.graphEdge.fromVertex.x === this.graphEdge.toVertex.x && this.graphEdge.fromVertex.y === this.graphEdge.toVertex.y) {
      this.renderData = [];
      return;
    }

    this.lineWidth = this.computeLineWidth(display, true);
    const fromOffsetPx = this.fromOffset * this.lineWidth;
    const toOffsetPx = this.toOffset * this.lineWidth;

    if (this.useGeographicRendering && this.graphEdge.geomCoords) {
      this.renderData = this.graphEdge.getGeometricCoords(fromOffsetPx, toOffsetPx, display, this.forward);
    } else {
      this.renderData = this.graphEdge.getRenderCoords(fromOffsetPx, toOffsetPx, display, this.forward);
    }

    const firstRenderPoint = this.renderData[0];
    const lastRenderPoint = this.renderData[this.renderData.length - 1];
    let pt;

    if (!this.graphEdge.fromVertex.isInternal) {
      pt = this.forward ? firstRenderPoint : lastRenderPoint;

      if (pt) {
        this.graphEdge.fromVertex.point.addRenderData({
          rEdge: this,
          x: pt.x,
          y: pt.y
        });
      }
    }

    pt = this.forward ? lastRenderPoint : firstRenderPoint;

    if (pt) {
      this.graphEdge.toVertex.point.addRenderData({
        rEdge: this,
        x: pt.x,
        y: pt.y
      });
    }

    forEach(this.graphEdge.pointArray, (point, i) => {
      if (point.getType() === 'TURN') return;
      const t = (i + 1) / (this.graphEdge.pointArray.length + 1);
      const coord = this.graphEdge.coordAlongEdge(this.forward ? t : 1 - t, this.renderData, display);

      if (coord) {
        point.addRenderData({
          rEdge: this,
          x: coord.x,
          y: coord.y
        });
      }
    });
  }

  computeLineWidth(display, includeEnvelope) {
    const styler = display.styler;

    if (styler && display) {
      // compute the line width
      const env = styler.compute(styler.segments.envelope, display, this);

      if (env && includeEnvelope) {
        return parseFloat(env.substring(0, env.length - 2), 10) - 2;
      } else {
        const lw = styler.compute(styler.segments['stroke-width'], display, this);
        return parseFloat(lw.substring(0, lw.length - 2), 10) - 2;
      }
    }
  }

  isFocused() {
    return this.focused === true;
  }

  getZIndex() {
    return 10000;
  }
  /**
   *  Computes the point of intersection between two adjacent, offset RenderedEdges (the
   *  edge the function is called on and a second edge passed as a parameter)
   *  by "extending" the adjacent edges and finding the point of intersection. If
   *  such a point exists, the existing renderData arrays for the edges are
   *  adjusted accordingly, as are any associated stops.
   */


  intersect(rEdge) {
    // do no intersect adjacent edges of unequal bundle size
    if (this.graphEdge.renderedEdges.length !== rEdge.graphEdge.renderedEdges.length) {
      return;
    }

    const commonVertex = this.graphEdge.commonVertex(rEdge.graphEdge);
    if (!commonVertex || commonVertex.point.isSegmentEndPoint) return;
    const thisCheck = commonVertex === this.graphEdge.fromVertex && this.forward || commonVertex === this.graphEdge.toVertex && !this.forward;
    const otherCheck = commonVertex === rEdge.graphEdge.fromVertex && rEdge.forward || commonVertex === rEdge.graphEdge.toVertex && !rEdge.forward;
    const p1 = thisCheck ? this.renderData[0] : this.renderData[this.renderData.length - 1];
    const v1 = this.graphEdge.getVector(commonVertex);
    const p2 = otherCheck ? rEdge.renderData[0] : rEdge.renderData[rEdge.renderData.length - 1];
    const v2 = rEdge.graphEdge.getVector(commonVertex);
    if (!p1 || !p2 || !v1 || !v2 || p1.x === p2.x && p1.y === p2.y) return;
    const isect = lineIntersection(p1.x, p1.y, p1.x + v1.x, p1.y - v1.y, p2.x, p2.y, p2.x + v2.x, p2.y - v2.y);
    if (!isect.intersect) return; // adjust the endpoint of the first edge

    if (thisCheck) {
      this.renderData[0].x = isect.x;
      this.renderData[0].y = isect.y;
    } else {
      this.renderData[this.renderData.length - 1].x = isect.x;
      this.renderData[this.renderData.length - 1].y = isect.y;
    } // adjust the endpoint of the second edge


    if (otherCheck) {
      rEdge.renderData[0].x = isect.x;
      rEdge.renderData[0].y = isect.y;
    } else {
      rEdge.renderData[rEdge.renderData.length - 1].x = isect.x;
      rEdge.renderData[rEdge.renderData.length - 1].y = isect.y;
    } // update the point renderData


    commonVertex.point.addRenderData({
      rEdge: this,
      x: isect.x,
      y: isect.y
    });
  }

  findExtension(vertex) {
    const incidentEdges = vertex.incidentEdges(this.graphEdge);
    const bundlerId = this.patternIds || this.pathSegmentIds;

    for (let e = 0; e < incidentEdges.length; e++) {
      const edgeSegments = incidentEdges[e].renderedEdges;

      for (let s = 0; s < edgeSegments.length; s++) {
        const segment = edgeSegments[s];
        const otherId = segment.patternIds || segment.pathSegmentIds;

        if (bundlerId === otherId) {
          return segment;
        }
      }
    }
  }

  toString() {
    return `RenderedEdge ${this.id} type=${this.type} on ${this.graphEdge.toString()} w/ patterns ${this.patternIds} fwd=${this.forward}`;
  }

}
/**
 * Helper method to construct a merged ID string from a list of items with
 * their own IDs
 */

function constuctIdListString(items) {
  const idArr = [];
  forEach(items, item => {
    idArr.push(item.getId());
  });
  idArr.sort();
  return idArr.join(',');
}

/**
 * RenderedSegment
 */

let rSegmentId = 0;
class RenderedSegment {
  constructor(pathSegment) {
    this.id = rSegmentId++;
    this.renderedEdges = [];
    this.pathSegment = pathSegment;
    if (pathSegment) this.type = pathSegment.type;
    this.focused = true;
  }

  getId() {
    return this.id;
  }

  getType() {
    return this.type;
  }

  addRenderedEdge(rEdge) {
    this.renderedEdges.push(rEdge);
  }

  render(display) {
    const styler = display.styler;
    this.renderData = [];
    forEach(this.renderedEdges, rEdge => {
      this.renderData = this.renderData.concat(rEdge.renderData);
    }); // Check if this segment is to be drawn as an arc; if so replace render data

    if (this.pathSegment.journeySegment && this.pathSegment.journeySegment.arc) {
      const first = this.renderData[0];
      const last = this.renderData[this.renderData.length - 1];
      const v = {
        x: last.x - first.x,
        y: last.y - first.y
      };
      const vp = rotateVector(normalizeVector(v), -Math.PI / 2);
      const dist = distance(first.x, first.y, last.x, last.y);
      const arc = {
        arc: -45,
        ex: (last.x + first.x) / 2 + vp.x * (dist / 4),
        ey: (last.y + first.y) / 2 + vp.y * (dist / 4),
        radius: dist * 0.75,
        x: last.x,
        y: last.y
      };
      this.renderData = [first, arc, last];
    }

    display.drawPath(this.renderData, {
      fill: 'none',
      stroke: styler.compute2('segments', 'stroke', this),
      'stroke-dasharray': styler.compute2('segments', 'stroke-dasharray', this),
      'stroke-linecap': styler.compute2('segments', 'stroke-linecap', this),
      'stroke-width': styler.compute2('segments', 'stroke-width', this)
    });
  }

  setFocused(focused) {
    this.focused = focused;
  }

  isFocused() {
    return this.focused;
  }

  runFocusTransition(display, callback) {
    const newColor = display.styler.compute(display.styler.segments.stroke, display, this);
    this.lineGraph.transition().style('stroke', newColor).call(callback);
  }

  getZIndex() {
    return this.zIndex;
  }

  computeLineWidth(display, includeEnvelope) {
    const styler = display.styler;

    if (styler && display) {
      // compute the line width
      const env = styler.compute(styler.segments.envelope, display, this);

      if (env && includeEnvelope) {
        return parseFloat(env.substring(0, env.length - 2), 10) - 2;
      } else {
        const lw = styler.compute(styler.segments['stroke-width'], display, this);
        return parseFloat(lw.substring(0, lw.length - 2), 10) - 2;
      }
    }
  }

  compareTo(other) {
    // show transit segments in front of other types
    if (this.type === 'TRANSIT' && other.type !== 'TRANSIT') return -1;
    if (other.type === 'TRANSIT' && this.type !== 'TRANSIT') return 1;

    if (this.type === 'TRANSIT' && other.type === 'TRANSIT') {
      // for two transit segments, try sorting transit mode first
      if (this.mode && other.mode && this.mode !== other.mode) {
        return this.mode > other.mode;
      } // for two transit segments of the same mode, sort by id (for display consistency)


      return this.getId() < other.getId();
    }
  }

  getLabelTextArray() {
    const textArray = [];
    forEach(this.patterns, pattern => {
      // TODO: Should we attempt to extract part of the long name if short name
      // is missing?
      const shortName = pattern.route.route_short_name;
      if (textArray.indexOf(shortName) === -1) textArray.push(shortName);
    });
    return textArray;
  }

  getLabelAnchors(display, spacing) {
    const labelAnchors = [];
    this.computeRenderLength(display);
    const anchorCount = Math.floor(this.renderLength / spacing);
    const pctSpacing = spacing / this.renderLength;

    for (let i = 0; i < anchorCount; i++) {
      const t = i % 2 === 0 ? 0.5 + i / 2 * pctSpacing : 0.5 - (i + 1) / 2 * pctSpacing;
      const coord = this.coordAlongRenderedPath(t, display);
      if (coord) labelAnchors.push(coord);
    }

    return labelAnchors;
  }

  coordAlongRenderedPath(t, display) {
    const loc = t * this.renderLength;
    let cur = 0;

    for (let i = 0; i < this.renderedEdges.length; i++) {
      const rEdge = this.renderedEdges[i];
      const edgeRenderLen = rEdge.graphEdge.getRenderLength(display);

      if (loc <= cur + edgeRenderLen) {
        const t2 = (loc - cur) / edgeRenderLen;
        return rEdge.graphEdge.coordAlongEdge(t2, rEdge.renderData, display);
      }

      cur += edgeRenderLen;
    }
  }

  computeRenderLength(display) {
    this.renderLength = 0;
    forEach(this.renderedEdges, rEdge => {
      this.renderLength += rEdge.graphEdge.getRenderLength(display);
    });
  }

  toString() {
    return `RenderedSegment ${this.id} on ${this.pathSegment ? this.pathSegment.toString() : ' (null segment)'}`;
  }

}

/**
 * Edge
 */

let edgeId = 0;
class Edge {
  /**
   * Initialize a new edge
   * @constructor
   * @param {Point[]} pointArray - the internal Points for this Edge
   * @param {Vertex} fromVertex
   * @param {Vertex} toVertex
   */
  constructor(pointArray, fromVertex, toVertex) {
    this.id = edgeId++;
    this.pointArray = pointArray;
    this.fromVertex = fromVertex;
    this.toVertex = toVertex;
    this.pathSegments = [];
    this.renderedEdges = [];
  }

  getId() {
    return this.id;
  }
  /**
   *
   */


  getLength() {
    const dx = this.toVertex.x - this.fromVertex.x;
    const dy = this.toVertex.y - this.fromVertex.y;
    return Math.sqrt(dx * dx + dy * dy);
  }

  getWorldLength() {
    if (!this.worldLength) this.calculateWorldLengthAndMidpoint();
    return this.worldLength;
  }

  getWorldMidpoint() {
    if (!this.worldMidpoint) this.calculateWorldLengthAndMidpoint();
    return this.worldMidpoint;
  }

  calculateWorldLengthAndMidpoint() {
    const allPoints = [this.fromVertex.point].concat(this.pointArray, [this.toVertex.point]);
    this.worldLength = 0;

    for (var i = 0; i < allPoints.length - 1; i++) {
      this.worldLength += distance(allPoints[i].worldX, allPoints[i].worldY, allPoints[i + 1].worldX, allPoints[i + 1].worldY);
    }

    if (this.worldLength === 0) {
      this.worldMidpoint = {
        x: this.fromVertex.point.worldX,
        y: this.fromVertex.point.worldY
      };
    } else {
      let distTraversed = 0;

      for (i = 0; i < allPoints.length - 1; i++) {
        const dist = distance(allPoints[i].worldX, allPoints[i].worldY, allPoints[i + 1].worldX, allPoints[i + 1].worldY);

        if ((distTraversed + dist) / this.worldLength >= 0.5) {
          // find the position along this segment (0 <= t <= 1) where the edge midpoint lies
          const t = (0.5 - distTraversed / this.worldLength) / (dist / this.worldLength);
          this.worldMidpoint = {
            x: allPoints[i].worldX + t * (allPoints[i + 1].worldX - allPoints[i].worldX),
            y: allPoints[i].worldY + t * (allPoints[i + 1].worldY - allPoints[i].worldY)
          };
          this.pointsBeforeMidpoint = i;
          this.pointsAfterMidpoint = this.pointArray.length - i;
          break;
        }

        distTraversed += dist;
      }
    }
  }
  /**
   *
   */


  isAxial() {
    return this.toVertex.x === this.fromVertex.x || this.toVertex.y === this.fromVertex.y;
  }
  /**
   *
   */


  hasCurvature() {
    return this.elbow !== null;
  }
  /**
   *
   */


  replaceVertex(oldVertex, newVertex) {
    if (oldVertex === this.fromVertex) this.fromVertex = newVertex;
    if (oldVertex === this.toVertex) this.toVertex = newVertex;
  }
  /**
   *  Add a path segment that traverses this edge
   */


  addPathSegment(segment) {
    this.pathSegments.push(segment);
  }

  copyPathSegments(baseEdge) {
    forEach(baseEdge.pathSegments, pathSegment => {
      this.addPathSegment(pathSegment);
    });
  }

  getPathSegmentIds(baseEdge) {
    const pathSegIds = this.pathSegments.map(segment => segment.id);
    pathSegIds.sort();
    return pathSegIds.join(',');
  }
  /**
   *
   */


  addRenderedEdge(rEdge) {
    if (this.renderedEdges.indexOf(rEdge) !== -1) return;
    this.renderedEdges.push(rEdge);
  }
  /** internal geometry functions **/


  calculateGeometry(cellSize, angleConstraint) {
    // if(!this.hasTransit()) angleConstraint = 5;
    angleConstraint = angleConstraint || 45;
    this.angleConstraintR = angleConstraint * Math.PI / 180;
    this.fx = this.fromVertex.point.worldX;
    this.fy = this.fromVertex.point.worldY;
    this.tx = this.toVertex.point.worldX;
    this.ty = this.toVertex.point.worldY;
    const midpoint = this.getWorldMidpoint();
    const targetFromAngle = getVectorAngle(midpoint.x - this.fx, midpoint.y - this.fy);
    this.constrainedFromAngle = Math.round(targetFromAngle / this.angleConstraintR) * this.angleConstraintR;
    const fromAngleDelta = Math.abs(this.constrainedFromAngle - targetFromAngle);
    this.fvx = Math.cos(this.constrainedFromAngle);
    this.fvy = Math.sin(this.constrainedFromAngle);
    const targetToAngle = getVectorAngle(midpoint.x - this.tx, midpoint.y - this.ty);
    this.constrainedToAngle = Math.round(targetToAngle / this.angleConstraintR) * this.angleConstraintR;
    const toAngleDelta = Math.abs(this.constrainedToAngle - targetToAngle);
    this.tvx = Math.cos(this.constrainedToAngle);
    this.tvy = Math.sin(this.constrainedToAngle);
    const tol = 0.01;
    const v = normalizeVector({
      x: this.toVertex.x - this.fromVertex.x,
      y: this.toVertex.y - this.fromVertex.y
    }); // check if we need to add curvature

    if (!equalVectors(this.fvx, this.fvy, -this.tvx, -this.tvy, tol) || !equalVectors(this.fvx, this.fvy, v.x, v.y, tol)) {
      // see if the default endpoint angles produce a valid intersection
      const isect = this.computeEndpointIntersection();

      if (isect.intersect) {
        // if so, compute the elbow and we're done
        this.elbow = {
          x: this.fx + isect.u * this.fvx,
          y: this.fy + isect.u * this.fvy
        };
      } else {
        // if not, adjust the two endpoint angles until they properly intersect
        // default test: compare angle adjustments (if significant difference)
        if (Math.abs(fromAngleDelta - toAngleDelta) > 0.087) {
          if (fromAngleDelta < toAngleDelta) {
            this.adjustToAngle();
          } else {
            this.adjustFromAngle();
          }
        } else {
          // secondary test: look at distribution of shapepoints
          if (this.pointsAfterMidpoint < this.pointsBeforeMidpoint) {
            this.adjustToAngle();
          } else {
            this.adjustFromAngle();
          }
        }
      }
    }

    this.fromAngle = this.constrainedFromAngle;
    this.toAngle = this.constrainedToAngle;
    this.calculateVectors();
    this.calculateAlignmentIds();
  }
  /**
   *  Adjust the 'to' endpoint angle by rotating it increments of angleConstraintR
   *  until a valid intersection between the from and to endpoint rays is achieved.
   */


  adjustToAngle() {
    const isCcw = ccw(this.fx, this.fy, this.fx + this.fvx, this.fy + this.fvy, this.tx, this.ty);
    const delta = isCcw > 0 ? this.angleConstraintR : -this.angleConstraintR;
    let i = 0;
    let isect;

    while (i++ < 100) {
      this.constrainedToAngle += delta;
      this.tvx = Math.cos(this.constrainedToAngle);
      this.tvy = Math.sin(this.constrainedToAngle);
      isect = this.computeEndpointIntersection();
      if (isect.intersect) break;
    }

    this.elbow = {
      x: this.fx + isect.u * this.fvx,
      y: this.fy + isect.u * this.fvy
    };
  }
  /**
   *  Adjust the 'from' endpoint angle by rotating it increments of angleConstraintR
   *  until a valid intersection between the from and to endpoint rays is achieved.
   */


  adjustFromAngle() {
    const isCcw = ccw(this.tx, this.ty, this.tx + this.tvx, this.ty + this.tvy, this.fx, this.fy);
    const delta = isCcw > 0 ? this.angleConstraintR : -this.angleConstraintR;
    let i = 0;
    let isect;

    while (i++ < 100) {
      this.constrainedFromAngle += delta;
      this.fvx = Math.cos(this.constrainedFromAngle);
      this.fvy = Math.sin(this.constrainedFromAngle);
      isect = this.computeEndpointIntersection();
      if (isect.intersect) break;
    }

    this.elbow = {
      x: this.fx + isect.u * this.fvx,
      y: this.fy + isect.u * this.fvy
    };
  }

  computeEndpointIntersection() {
    return rayIntersection(this.fx, this.fy, this.fvx, this.fvy, this.tx, this.ty, this.tvx, this.tvy);
  }

  calculateVectors(fromAngle, toAngle) {
    this.fromVector = {
      x: Math.cos(this.fromAngle),
      y: Math.sin(this.fromAngle)
    };
    this.fromleftVector = {
      x: -this.fromVector.y,
      y: this.fromVector.x
    };
    this.fromRightVector = {
      x: this.fromVector.y,
      y: -this.fromVector.x
    };
    this.toVector = {
      x: Math.cos(this.toAngle + Math.PI),
      y: Math.sin(this.toAngle + Math.PI)
    };
    this.toleftVector = {
      x: -this.toVector.y,
      y: this.toVector.x
    };
    this.toRightVector = {
      x: this.toVector.y,
      y: -this.toVector.x
    };
  }
  /**
   *  Compute the 'alignment id', a string that uniquely identifies a line in
   *  2D space given a point and angle relative to the x-axis.
   */


  calculateAlignmentId(x, y, angle) {
    let angleD = Math.round(angle * 180 / Math.PI);
    if (angleD > 90) angleD -= 180;
    if (angleD <= -90) angleD += 180;

    if (angleD === 90) {
      return '90_x' + x;
    } // calculate the y-axis crossing


    const ya = Math.round(y - x * Math.tan(angle));
    return angleD + '_y' + ya;
  }

  calculateAlignmentIds() {
    this.fromAlignmentId = this.calculateAlignmentId(this.fromVertex.x, this.fromVertex.y, this.fromAngle);
    this.toAlignmentId = this.calculateAlignmentId(this.toVertex.x, this.toVertex.y, this.toAngle);
  }

  hasTransit(cellSize) {
    // debug(this);
    for (let i = 0; i < this.pathSegments.length; i++) {
      if (this.pathSegments[i].getType() === 'TRANSIT') {
        return true;
      }
    }

    return false;
  }

  getFromAlignmentId() {
    return this.fromAlignmentId;
  }

  getToAlignmentId() {
    return this.toAlignmentId;
  }

  getAlignmentRange(alignmentId) {
    let p1, p2;

    if (alignmentId === this.fromAlignmentId) {
      p1 = this.fromVertex;
      p2 = this.elbow || this.toVertex;
    } else if (alignmentId === this.toAlignmentId) {
      p1 = this.toVertex;
      p2 = this.elbow || this.fromVertex;
    } else {
      return null;
    }

    let max, min;

    if (alignmentId.substring(0, 2) === '90') {
      min = Math.min(p1.y, p2.y);
      max = Math.max(p1.y, p2.y);
    } else {
      min = Math.min(p1.x, p2.x);
      max = Math.max(p1.x, p2.x);
    }

    return {
      max: max,
      min: min
    };
  }

  align(vertex, vector) {
    if (this.aligned || !this.hasCurvature()) return;
    const currentVector = this.getVector(vertex);

    if (Math.abs(currentVector.x) !== Math.abs(vector.x) || Math.abs(currentVector.y) !== Math.abs(vector.y)) {
      this.curveAngle = -this.curveAngle;
      this.calculateGeometry();
    }

    this.aligned = true;
  }

  getGeometricCoords(fromOffsetPx, toOffsetPx, display, forward) {
    const coords = []; // reverse the coords array if needed

    const geomCoords = forward ? this.geomCoords : this.geomCoords.concat().reverse();
    forEach(geomCoords, (coord, i) => {
      let fromVector = null;
      let toVector = null;
      let rightVector;
      let xOffset, yOffset;
      const x1 = display.xScale.compute(coord[0]);
      const y1 = display.yScale.compute(coord[1]); // calculate the vector leading in to this coordinate

      if (i > 0) {
        const prevCoord = geomCoords[i - 1];
        const x0 = display.xScale.compute(prevCoord[0]);
        const y0 = display.yScale.compute(prevCoord[1]);
        if (x1 === x0 && y1 === y0) return;
        toVector = {
          x: x1 - x0,
          y: y1 - y0
        };
      } // calculate the vector leading out from this coordinate


      if (i < geomCoords.length - 1) {
        const nextCoord = geomCoords[i + 1];
        const x2 = display.xScale.compute(nextCoord[0]);
        const y2 = display.yScale.compute(nextCoord[1]);
        if (x2 === x1 && y2 === y1) return;
        fromVector = {
          x: x2 - x1,
          y: y2 - y1
        };
      }

      if (fromVector && !toVector) {
        // the first point in the geomCoords sequence
        rightVector = normalizeVector({
          x: fromVector.y,
          y: -fromVector.x
        });
        xOffset = fromOffsetPx * rightVector.x;
        yOffset = fromOffsetPx * rightVector.y;
      } else if (!fromVector && toVector) {
        // the last point in the geomCoords sequence
        rightVector = normalizeVector({
          x: toVector.y,
          y: -toVector.x
        });
        xOffset = fromOffsetPx * rightVector.x;
        yOffset = fromOffsetPx * rightVector.y;
      } else {
        // an internal point
        rightVector = normalizeVector({
          x: fromVector.y,
          y: -fromVector.x
        });
        xOffset = fromOffsetPx * rightVector.x;
        yOffset = fromOffsetPx * rightVector.y; // TODO: properly compute the offsets based on both vectors
      }

      coords.push({
        x: x1 + xOffset,
        y: y1 + yOffset
      });
    });
    return coords;
  }
  /**
   * Get render coords for the provided offsets (0 values for offsets imply base
   * render coordinates).
   */


  getRenderCoords(fromOffsetPx, toOffsetPx, display, forward) {
    const isBase = fromOffsetPx === 0 && toOffsetPx === 0;

    if (!this.baseRenderCoords && !isBase) {
      this.calculateBaseRenderCoords(display);
    }

    const fromOffsetX = fromOffsetPx * this.fromRightVector.x;
    const fromOffsetY = fromOffsetPx * this.fromRightVector.y;
    const toOffsetX = toOffsetPx * this.toRightVector.x;
    const toOffsetY = toOffsetPx * this.toRightVector.y;
    const fx = this.fromVertex.getRenderX(display) + fromOffsetX;
    const fy = this.fromVertex.getRenderY(display) - fromOffsetY;
    const fvx = this.fromVector.x;
    const fvy = -this.fromVector.y;
    const tx = this.toVertex.getRenderX(display) + toOffsetX;
    const ty = this.toVertex.getRenderY(display) - toOffsetY;
    const tvx = -this.toVector.x;
    const tvy = this.toVector.y;
    const coords = []; // append the first ('from') coordinate

    coords.push({
      x: forward ? fx : tx,
      y: forward ? fy : ty
    });
    let len = null;
    let x1;
    let y1;
    let x2;
    let y2; // determine if this edge has an elbow, i.e. a bend in the middle

    if (isBase && !this.isStraight() || !isBase && this.baseRenderCoords.length === 4) {
      const isect = rayIntersection(fx, fy, fvx, fvy, tx, ty, tvx, tvy);

      if (isect.intersect) {
        const u = isect.u;
        const ex = fx + fvx * u;
        const ey = fy + fvy * u;
        this.ccw = ccw(fx, fy, ex, ey, tx, ty); // calculate the angle of the arc

        const angleR = this.getElbowAngle(); // calculate the radius of the arc in pixels, taking offsets into consideration

        const rPx = this.getBaseRadiusPx() - this.ccw * (fromOffsetPx + toOffsetPx) / 2; // calculate the distance from the elbow to place the arc endpoints in each direction

        let d = rPx * Math.tan(angleR / 2); // make sure the arc endpoint placement distance is not longer than the either of the
        // elbow-to-edge-endpoint distances

        const l1 = distance(fx, fy, ex, ey);
        const l2 = distance(tx, ty, ex, ey);
        d = Math.min(Math.min(l1, l2), d);
        x1 = ex - this.fromVector.x * d;
        y1 = ey + this.fromVector.y * d;
        x2 = ex + this.toVector.x * d;
        y2 = ey - this.toVector.y * d;
        const radius = getRadiusFromAngleChord(angleR, distance(x1, y1, x2, y2));
        const arc = angleR * (180 / Math.PI) * (this.ccw < 0 ? 1 : -1);

        if (forward) {
          coords.push({
            len: distance(fx, fy, x1, y1),
            x: x1,
            y: y1
          });
          coords.push({
            arc: arc,
            ex,
            ey,
            len: angleR * radius,
            radius: radius,
            x: x2,
            y: y2
          });
          len = distance(x2, y2, tx, ty);
        } else {
          // backwards traversal
          coords.push({
            len: distance(tx, ty, x2, y2),
            x: x2,
            y: y2
          });
          coords.push({
            arc: -arc,
            len: angleR * radius,
            radius: radius,
            x: x1,
            y: y1
          });
          len = distance(x1, y1, fx, fy);
        }
      }
    } // if the length wasn't calculated during elbow-creation, do it now


    if (len === null) len = distance(fx, fy, tx, ty); // append the final ('to') coordinate

    coords.push({
      len: len,
      x: forward ? tx : fx,
      y: forward ? ty : fy
    });
    return coords;
  }

  calculateBaseRenderCoords(display) {
    this.baseRenderCoords = this.getRenderCoords(0, 0, display, true);
  }

  isStraight() {
    const tol = 0.00001;
    return Math.abs(this.fromVector.x - this.toVector.x) < tol && Math.abs(this.fromVector.y - this.toVector.y) < tol;
  }

  getBaseRadiusPx() {
    return 15;
  }

  getElbowAngle() {
    const cx = this.fromVector.x - this.toVector.x;
    const cy = this.fromVector.y - this.toVector.y;
    const c = Math.sqrt(cx * cx + cy * cy) / 2;
    const theta = Math.asin(c);
    return theta * 2;
  }

  getRenderLength(display) {
    if (!this.baseRenderCoords) this.calculateBaseRenderCoords(display);

    if (!this.renderLength) {
      this.renderLength = 0;

      for (let i = 1; i < this.baseRenderCoords.length; i++) {
        this.renderLength += this.baseRenderCoords[i].len;
      }
    }

    return this.renderLength;
  }
  /**
   * Retrieve the coordinate located at a defined percentage along an Edge's length.
   * @param {Number} t - a value between 0 and 1 representing the location of the
   *   point to be computed
   * @param {Object[]} coords - the offset coordinates computed for this edge.
   * @param {Display} display
   * @returns {Object} - the coordinate as an {x,y} Object
   */


  coordAlongEdge(t, coords, display) {
    if (!this.baseRenderCoords) {
      this.calculateBaseRenderCoords(display);
    }

    if (coords.length !== this.baseRenderCoords.length) {
      // If the render edge coordinates do not match the base render coordinates,
      // get coordinate along "offset edge."
      return this.coordAlongOffsetEdge(t, coords, display);
    } // get the length of this edge in screen units using the "base" (i.e. un-offset) render coords


    const len = this.getRenderLength(); // the target distance along the Edge's base geometry

    const loc = t * len;
    return this.coordAlongRefEdge(loc, coords, this.baseRenderCoords);
  }
  /**
   * Get coordinate along "offset edge."
   * @param {[type]} t - a value between 0 and 1 representing the location of the
   *   point to be computed
   * @param {Object[]} coords - the offset coordinates computed for this edge.
   * @returns {Object} - the coordinate as an {x,y} Object
   */


  coordAlongOffsetEdge(t, coords) {
    // Get total length of edge by summing inter-coordinate distances.
    let len = 0;

    for (let i = 1; i < coords.length; i++) {
      const c0 = coords[i - 1];
      const c = coords[i];

      if (!c.len) {
        // If length between coord not available, calculate.
        c.len = distance(c0.x, c0.y, c.x, c.y);
      }

      len += c.len;
    } // the target distance along the Edge's base geometry


    const loc = t * len;
    return this.coordAlongRefEdge(loc, coords);
  }
  /**
   * Iterate over reference coordinates (which default to input coords) to find
   * the coordinate along the edge at distance along edge.
   * @param  {[type]} targetDistance - target distance along edge's base geometry
   * @param  {[type]} coords - coordinates for edge
   * @param  {[type]} [refCoordinates=coords] - reference coordinates to use for
   *                                            distance. Defaults to coords.
   * @returns {Object} - the coordinate as an {x,y} Object
   */


  coordAlongRefEdge(targetDistance, coords, refCoordinates = coords) {
    // our current location along the edge (in world units)
    let currentLocation = 0;

    for (let i = 1; i < refCoordinates.length; i++) {
      const distanceToNextCoord = refCoordinates[i].len;
      const {
        arc,
        radius,
        x,
        y
      } = coords[i];

      if (targetDistance < currentLocation + distanceToNextCoord) {
        // Location falls within the previous and next coordinates. Calculate
        // percentage along segment.
        const percentAlong = (targetDistance - currentLocation) / distanceToNextCoord;

        if (arc) {
          // Generate coordinate on arc segment
          const theta = Math.PI * arc / 180;
          const [c0, c1, c2] = coords;
          const isCcw = ccw(c0.x, c0.y, c1.x, c1.y, c2.x, c2.y);
          return pointAlongArc(c1.x, c1.y, c2.x, c2.y, radius, theta, isCcw, percentAlong);
        } else {
          // Generate coordinate on straight segment
          const dx = x - coords[i - 1].x;
          const dy = y - coords[i - 1].y;
          return {
            x: coords[i - 1].x + dx * percentAlong,
            y: coords[i - 1].y + dy * percentAlong
          };
        }
      }

      currentLocation += distanceToNextCoord;
    }
  }

  clearRenderData() {
    this.baseRenderCoords = null;
    this.renderLength = null;
  }

  getVector(vertex) {
    if (vertex === this.fromVertex) return this.fromVector;
    if (vertex === this.toVertex) return this.toVector;
  }
  /**
   *  Gets the vertex opposite another vertex on an edge
   */


  oppositeVertex(vertex) {
    if (vertex === this.toVertex) return this.fromVertex;
    if (vertex === this.fromVertex) return this.toVertex;
    return null;
  }

  commonVertex(edge) {
    if (this.fromVertex === edge.fromVertex || this.fromVertex === edge.toVertex) {
      return this.fromVertex;
    }

    if (this.toVertex === edge.fromVertex || this.toVertex === edge.toVertex) {
      return this.toVertex;
    }

    return null;
  }
  /**
   *
   */


  setPointLabelPosition(pos, skip) {
    if (this.fromVertex.point !== skip) {
      this.fromVertex.point.labelPosition = pos;
    }

    if (this.toVertex.point !== skip) this.toVertex.point.labelPosition = pos;
    forEach(this.pointArray, point => {
      if (point !== skip) point.labelPosition = pos;
    });
  }
  /**
   *  Determines if this edge is part of a standalone, non-transit path
   *  (e.g. a walk/bike/drive-only journey)
   */


  isNonTransitPath() {
    return this.pathSegments.length === 1 && this.pathSegments[0] !== 'TRANSIT' && this.pathSegments[0].path.segments.length === 1;
  }
  /**
   *
   */


  toString() {
    return 'Edge ' + this.getId() + ' (' + this.fromVertex.toString() + ' to ' + this.toVertex.toString() + ')';
  }

}
/** helper functions **/

function equalVectors(x1, y1, x2, y2, tol) {
  tol = tol || 0;
  return Math.abs(x1 - x2) < tol && Math.abs(y1 - y2) < tol;
}

/**
 *  A group of edges that share the same endpoint vertices
 */

class EdgeGroup {
  constructor(fromVertex, toVertex, type) {
    this.fromVertex = fromVertex;
    this.toVertex = toVertex;
    this.type = type;
    this.edges = [];
    this.commonPoints = null;
    this.worldLength = 0;
  }

  addEdge(edge) {
    this.edges.push(edge);
    edge.edgeGroup = this; // update the groups worldLength

    this.worldLength = Math.max(this.worldLength, edge.getWorldLength());

    if (this.commonPoints === null) {
      // if this is first edge added, initialize group's commonPoint array to include all of edge's points
      this.commonPoints = edge.pointArray.slice();
    } else {
      // otherwise, update commonPoints array to only include those in added edge
      this.commonPoints = this.commonPoints.concat(edge.pointArray.filter(pt => this.commonPoints.indexOf(pt) !== -1));
    }
  }

  getWorldLength() {
    return this.worldLength;
  }

  getInternalVertexPQ() {
    // create an array of all points on the edge (endpoints and internal)
    const allPoints = [this.fromVertex.point].concat(this.commonPoints, [this.toVertex.point]);
    const pq = new PriorityQueue(function (a, b) {
      return a.weight - b.weight;
    });

    for (let i = 1; i < allPoints.length - 1; i++) {
      const weight = this.getInternalVertexWeight(allPoints, i);
      pq.enq({
        point: allPoints[i],
        weight: weight
      });
    }

    return pq;
  }

  getInternalVertexWeight(pointArray, index) {
    const x1 = pointArray[index - 1].worldX;
    const y1 = pointArray[index - 1].worldY;
    const x2 = pointArray[index].worldX;
    const y2 = pointArray[index].worldY;
    const x3 = pointArray[index + 1].worldX;
    const y3 = pointArray[index + 1].worldY; // the weighting function is a combination of:
    // - the distances from this internal point to the two adjacent points, normalized for edge length (longer distances are prioritized)
    // - the angle formed by this point and the two adjacent ones ('sharper' angles are prioritized)

    const inDist = distance(x1, y1, x2, y2);
    const outDist = distance(x2, y2, x3, y3);
    const theta = angleFromThreePoints(x1, y1, x2, y2, x3, y3);
    const edgeLen = this.getWorldLength();
    const weight = inDist / edgeLen + outDist / edgeLen + Math.abs(Math.PI - theta) / Math.PI;
    return weight;
  }

  hasTransit() {
    for (let i = 0; i < this.edges.length; i++) {
      if (this.edges[i].hasTransit()) return true;
    }

    return false;
  }

  isNonTransitPath() {
    return this.edges.length === 1 && this.edges[0].isNonTransitPath();
  }

  getTurnPoints(maxAngle) {
    maxAngle = maxAngle || 0.75 * Math.PI;
    return this.commonPoints.filter(pt => pt.getType() === 'TURN' && Math.abs(pt.turnAngle) < maxAngle);
  }

}

/**
 * Vertex
 */
let vertexId = 0;
class Vertex {
  /**
   * Vertex constructor
   *
   * @param {Object} point the Point (a Stop, Place, etc.) attached to this vertex
   * @param {Number} x
   * @param {Number} y
   */
  constructor(point, x, y) {
    this.id = vertexId++;
    this.point = point;
    this.point.graphVertex = this;
    this.x = this.origX = x;
    this.y = this.origY = y;
    this.edges = [];
  }

  getId() {
    return this.id;
  }

  getRenderX(display) {
    return display.xScale.compute(this.x) + this.point.placeOffsets.x;
  }

  getRenderY(display) {
    return display.yScale.compute(this.y) + this.point.placeOffsets.y;
  }
  /**
   * Move to new coordinate
   *
   * @param {Number}
   * @param {Number}
   */


  moveTo(x, y) {
    this.x = x;
    this.y = y;
    /* this.edges.forEach(function (edge) {
      edge.calculateVectors();
    }); */
  }
  /**
   * Get array of edges incident to vertex. Allows specification of "incoming"
   * edge that will not be included in results.
   *
   * @param {Edge} inEdge optional incoming edge tp ignore
   */


  incidentEdges(inEdge) {
    return this.edges.filter(edge => edge !== inEdge);
  }
  /**
   * Add an edge to the vertex's edge list
   *
   * @param {Edge} edge
   */


  addEdge(edge) {
    const index = this.edges.indexOf(edge);
    if (index === -1) this.edges.push(edge);
  }
  /**
   * Remove an edge from the vertex's edge list
   *
   * @param {Edge} edge
   */


  removeEdge(edge) {
    const index = this.edges.indexOf(edge);
    if (index !== -1) this.edges.splice(index, 1);
  }

  toString() {
    return `Vertex ${this.getId()} (${this.point ? this.point.toString() : 'no point assigned'})`;
  }

}

const debug = /*#__PURE__*/require('debug')('transitive:graph');
/**
 *  A graph representing the underlying 'wireframe' network
 */


class NetworkGraph {
  constructor(network, vertices) {
    this.network = network;
    this.edges = [];
    this.vertices = [];
    /**
     *  Object mapping groups of edges that share the same two vertices.
     *  - Key is string of format A_B, where A and B are vertex IDs and A < B
     *  - Value is array of edges
     */

    this.edgeGroups = {}; // Add all base vertices

    for (const i in vertices) {
      this.addVertex(vertices[i], vertices[i].worldX, vertices[i].worldY);
    }
  }
  /**
   * Get the bounds of the graph in the graph's internal x/y coordinate space
   *
   * @return [[left, top], [right, bottom]]
   */


  bounds() {
    let xmax = null;
    let xmin = null;
    let ymax = null;
    let ymin = null;

    for (const i in this.vertices) {
      const vertex = this.vertices[i];
      xmin = xmin ? Math.min(xmin, vertex.x) : vertex.x;
      xmax = xmax ? Math.max(xmax, vertex.x) : vertex.x;
      ymin = ymin ? Math.min(ymin, vertex.y) : vertex.y;
      ymax = ymax ? Math.max(ymax, vertex.y) : vertex.y;
    }

    const maxExtent = 20037508.34;
    return [[xmin || -maxExtent, ymin || -maxExtent], [xmax || maxExtent, ymax || maxExtent]];
  }
  /**
   * Add Vertex
   */


  addVertex(point, x, y) {
    if (x === undefined || y === undefined) {
      const xy = sm.forward([point.getLon(), point.getLat()]);
      x = xy[0];
      y = xy[1];
    }

    const vertex = new Vertex(point, x, y);
    this.vertices.push(vertex);
    return vertex;
  }
  /**
   * Add Edge
   */


  addEdge(stops, from, to, segmentType) {
    if (this.vertices.indexOf(from) === -1 || this.vertices.indexOf(to) === -1) {
      debug('Error: Cannot add edge. Graph does not contain vertices.');
      return;
    }

    const edge = new Edge(stops, from, to);
    this.edges.push(edge);
    from.edges.push(edge);
    to.edges.push(edge);
    const groupKey = this.network.transitive.options.groupEdges ? this.getEdgeGroupKey(edge, segmentType) : edge.getId();

    if (!(groupKey in this.edgeGroups)) {
      this.edgeGroups[groupKey] = new EdgeGroup(edge.fromVertex, edge.toVertex, segmentType);
    }

    this.edgeGroups[groupKey].addEdge(edge);
    return edge;
  }

  removeEdge(edge) {
    // remove from the graph's edge collection
    const edgeIndex = this.edges.indexOf(edge);
    if (edgeIndex !== -1) this.edges.splice(edgeIndex, 1); // remove from any associated path segment edge lists

    forEach(edge.pathSegments, segment => {
      segment.removeEdge(edge);
    }); // remove from the endpoint vertex incidentEdge collections

    edge.fromVertex.removeEdge(edge);
    edge.toVertex.removeEdge(edge);
  }

  getEdgeGroup(edge) {
    return this.edgeGroups[this.getEdgeGroupKey(edge)];
  }

  getEdgeGroupKey(edge, segmentType) {
    return edge.fromVertex.getId() < edge.toVertex.getId() ? segmentType + '_' + edge.fromVertex.getId() + '_' + edge.toVertex.getId() : segmentType + '_' + edge.toVertex.getId() + '_' + edge.fromVertex.getId();
  }

  mergeVertices(vertexArray) {
    let xTotal = 0;
    let yTotal = 0;
    const vertexGroups = {
      MULTI: [],
      PLACE: [],
      STOP: [],
      TURN: []
    };
    forEach(vertexArray, vertex => {
      if (vertex.point.getType() in vertexGroups) {
        vertexGroups[vertex.point.getType()].push(vertex);
      }
    }); // don't merge stops and places, or multiple places:

    if (vertexGroups.STOP.length > 0 && vertexGroups.PLACE.length > 0 || vertexGroups.PLACE.length > 1 || vertexGroups.MULTI.length > 0) {
      return;
    }

    let mergePoint; // if merging turns with a place, create a new merged vertex around the place

    if (vertexGroups.PLACE.length === 1 && vertexGroups.TURN.length > 0) {
      mergePoint = vertexGroups.PLACE[0].point; // if merging turns with a single place, create a new merged vertex around the stop
    } else if (vertexGroups.STOP.length === 1 && vertexGroups.TURN.length > 0) {
      mergePoint = vertexGroups.STOP[0].point; // if merging multiple stops, create a new MultiPoint vertex
    } else if (vertexGroups.STOP.length > 1) {
      mergePoint = new MultiPoint();
      forEach(vertexGroups.STOP, stopVertex => {
        mergePoint.addPoint(stopVertex.point);
      }); // if merging multiple turns
    } else if (vertexGroups.TURN.length > 1) {
      mergePoint = vertexGroups.TURN[0].point;
    }

    if (!mergePoint) return;
    const mergedVertex = new Vertex(mergePoint, 0, 0);
    forEach(vertexArray, vertex => {
      xTotal += vertex.x;
      yTotal += vertex.y;
      forEach(vertex.edges.slice(), edge => {
        if (vertexArray.indexOf(edge.fromVertex) !== -1 && vertexArray.indexOf(edge.toVertex) !== -1) {
          this.removeEdge(edge);
          return;
        }

        edge.replaceVertex(vertex, mergedVertex);
        mergedVertex.addEdge(edge);
      });
      const index = this.vertices.indexOf(vertex);
      if (index !== -1) this.vertices.splice(index, 1);
    });
    mergedVertex.x = xTotal / vertexArray.length;
    mergedVertex.y = yTotal / vertexArray.length;
    mergedVertex.oldVertices = vertexArray;
    this.vertices.push(mergedVertex);
  }

  sortVertices() {
    this.vertices.sort((a, b) => {
      if (a.point && a.point.getType() === 'PLACE') return -1;
      if (b.point && b.point.getType() === 'PLACE') return 1;
      if (a.point && a.point.getType() === 'MULTI') return -1;
      if (b.point && b.point.getType() === 'MULTI') return 1;
      if (a.point && a.point.getType() === 'STOP') return -1;
      if (b.point && b.point.getType() === 'STOP') return 1;
    });
  }
  /**
   * Get the equivalent edge
   */


  getEquivalentEdge(pointArray, from, to) {
    for (let e = 0; e < this.edges.length; e++) {
      const edge = this.edges[e];

      if (edge.fromVertex === from && edge.toVertex === to && pointArray.length === edge.pointArray.length && equal(pointArray, edge.pointArray)) {
        return edge;
      }

      if (edge.fromVertex === to && edge.toVertex === from && pointArray.length === edge.pointArray.length && equal(pointArray.slice(0).reverse(), edge.pointArray)) {
        return edge;
      }
    }
  }
  /**
   *  Split a specified graph edge around a set of specified split points, where
   *  all split points are internal points of the edge to be split. A set of N
   *  valid split points will result in N+1 new edges. The original edge is
   *  removed from the graph.
   */


  splitEdgeAtInternalPoints(edge, points) {
    let subEdgePoints = [];
    let newEdge;
    const newEdgeInfoArr = [];
    let fromVertex = edge.fromVertex;
    let geomCoords = []; // iterate through the parent edge points, creating new sub-edges as needed

    forEach(edge.pointArray, (point, i) => {
      if (edge.pointGeom && i < edge.pointGeom.length) {
        geomCoords = geomCoords.concat(edge.pointGeom[i]);
      }

      if (points.indexOf(point) !== -1) {
        // we've reached a split point
        const x = point.worldX;
        const y = point.worldY;
        const newVertex = point.graphVertex || this.addVertex(point, x, y);
        newVertex.isInternal = true;
        newEdge = this.addEdge(subEdgePoints, fromVertex, newVertex, edge.edgeGroup.type);
        newEdge.isInternal = true;
        newEdge.copyPathSegments(edge);
        newEdgeInfoArr.push({
          fromVertex: fromVertex,
          graphEdge: newEdge
        });
        if (geomCoords.length > 0) newEdge.geomCoords = geomCoords;
        subEdgePoints = [];
        fromVertex = newVertex;
        geomCoords = [];
      } else {
        // otherwise, this point becomes an internal point of the new edge currently being created
        subEdgePoints.push(point);
      }
    }); // create the last sub-edge

    newEdge = this.addEdge(subEdgePoints, fromVertex, edge.toVertex, edge.edgeGroup.type);
    newEdge.isInternal = true;
    newEdge.copyPathSegments(edge);

    if (edge.pointGeom && edge.pointArray.length < edge.pointGeom.length) {
      geomCoords = geomCoords.concat(edge.pointGeom[edge.pointArray.length]);
    }

    if (geomCoords.length > 0) newEdge.geomCoords = geomCoords;
    newEdgeInfoArr.push({
      fromVertex: fromVertex,
      graphEdge: newEdge
    }); // insert the new edge sequence into the affected segments

    forEach(edge.pathSegments, pathSegment => {
      const indexInSegment = pathSegment.getEdgeIndex(edge);
      const forward = pathSegment.edges[indexInSegment].forward;
      let index = pathSegment.getEdgeIndex(edge);
      forEach(forward ? newEdgeInfoArr : newEdgeInfoArr.reverse(), edgeInfo => {
        pathSegment.insertEdgeAt(index, edgeInfo.graphEdge, forward ? edgeInfo.fromVertex : edgeInfo.toVertex);
        index++;
      });
    }); // remove the original edge from the graph

    this.removeEdge(edge);
  }
  /* collapseTransfers = function(threshold) {
    if(!threshold) return;
    this.edges.forEach(function(edge) {
      if (edge.getLength() > threshold ||
        edge.fromVertex.point.containsFromPoint() ||
        edge.fromVertex.point.containsToPoint() ||
        edge.toVertex.point.containsFromPoint() ||
        edge.toVertex.point.containsToPoint()) return;
      //if(edge.fromVertex.point.getType() === 'PLACE' || edge.toVertex.point.getType() === 'PLACE') return;
      var notTransit = true;
      edge.pathSegments.forEach(function(segment) {
        notTransit = notTransit && segment.type !== 'TRANSIT';
      });
      if (notTransit) {
        this.mergeVertices([edge.fromVertex, edge.toVertex]);
      }
    }, this);
  }; */


  pruneVertices() {
    forEach(this.vertices, vertex => {
      if (vertex.point.containsSegmentEndPoint()) return;
      const opposites = [];
      const pathSegmentBundles = {}; // maps pathSegment id list (string) to collection of edges (array)

      forEach(vertex.edges, edge => {
        const pathSegmentIds = edge.getPathSegmentIds();

        if (!(pathSegmentIds in pathSegmentBundles)) {
          pathSegmentBundles[pathSegmentIds] = [];
        }

        pathSegmentBundles[pathSegmentIds].push(edge);
        const opp = edge.oppositeVertex(vertex);
        if (opposites.indexOf(opp) === -1) opposites.push(opp);
      });
      if (opposites.length !== 2) return;

      for (const key in pathSegmentBundles) {
        const edgeArr = pathSegmentBundles[key];
        if (edgeArr.length === 2) this.mergeEdges(edgeArr[0], edgeArr[1]);
      }
    });
  }

  mergeEdges(edge1, edge2) {
    // check for infinite recursion loop case
    if (edge1.fromVertex === edge2.toVertex && edge2.fromVertex === edge1.toVertex) {
      return;
    } // reverse edges if necessary


    if (edge1.fromVertex === edge2.toVertex) {
      this.mergeEdges(edge2, edge1);
      return;
    }

    if (edge1.toVertex !== edge2.fromVertex) return; // edges cannot be merged

    const internalPoints = edge1.pointArray.concat(edge2.pointArray);
    const newEdge = this.addEdge(internalPoints, edge1.fromVertex, edge2.toVertex, edge1.edgeGroup.type);
    newEdge.pathSegments = edge1.pathSegments;
    forEach(newEdge.pathSegments, segment => {
      const i = segment.getEdgeIndex(edge1);
      segment.insertEdgeAt(i, newEdge, newEdge.fromVertex);
    }); // if both input edges are have coordinate geometry, merge the coords arrays in the new edge

    if (edge1.geomCoords && edge2.geomCoords) {
      newEdge.geomCoords = edge1.geomCoords.concat(edge2.geomCoords.length > 0 ? edge2.geomCoords.slice(1) : []);
    }

    debug('merging:');
    debug(edge1);
    debug(edge2);
    this.removeEdge(edge1);
    this.removeEdge(edge2);
  }

  snapToGrid(cellSize) {
    const coincidenceMap = {};
    forEach(this.vertices, vertex => {
      const nx = Math.round(vertex.x / cellSize) * cellSize;
      const ny = Math.round(vertex.y / cellSize) * cellSize;
      vertex.x = nx;
      vertex.y = ny;
      const key = nx + '_' + ny;
      if (!(key in coincidenceMap)) coincidenceMap[key] = [vertex];else coincidenceMap[key].push(vertex);
    });
    forEach(coincidenceMap, vertexArr => {
      if (vertexArr.length > 1) {
        this.mergeVertices(vertexArr);
      }
    });
  }

  calculateGeometry(cellSize, angleConstraint) {
    forEach(this.edges, edge => {
      edge.calculateGeometry(cellSize, angleConstraint);
    });
  }

  resetCoordinates() {
    forEach(this.vertices, vertex => {
      vertex.x = vertex.origX;
      vertex.y = vertex.origY;
    });
  }

  recenter() {
    const xCoords = [];
    const yCoords = [];
    forEach(this.vertices, v => {
      xCoords.push(v.x);
      yCoords.push(v.y);
    });
    const mx = d3.median(xCoords);
    const my = d3.median(yCoords);
    forEach(this.vertices, v => {
      v.x = v.x - mx;
      v.y = v.y - my;
    });
  }
  /** 2D line bundling & offsetting **/


  apply2DOffsets() {
    this.initComparisons();
    const alignmentBundles = {}; // maps alignment ID to array of range-bounded bundles on that alignment

    const addToBundle = (rEdge, alignmentId) => {
      let bundle; // compute the alignment range of the edge being bundled

      const range = rEdge.graphEdge.getAlignmentRange(alignmentId); // check if bundles already exist for this alignment

      if (!(alignmentId in alignmentBundles)) {
        // if not, create new and add to collection
        bundle = new AlignmentBundle();
        bundle.addEdge(rEdge, range.min, range.max);
        alignmentBundles[alignmentId] = [bundle]; // new AlignmentBundle();
      } else {
        // 1 or more bundles currently exist for this alignmentId
        const bundleArr = alignmentBundles[alignmentId]; // see if the segment range overlaps with that of an existing bundle

        for (let i = 0; i < bundleArr.length; i++) {
          if (bundleArr[i].rangeOverlaps(range.min, range.max)) {
            bundleArr[i].addEdge(rEdge, range.min, range.max);
            return;
          }
        } // ..if not, create a new bundle


        bundle = new AlignmentBundle();
        bundle.addEdge(rEdge, range.min, range.max);
        bundleArr.push(bundle);
      }
    };

    forEach(this.edges, edge => {
      const fromAlignmentId = edge.getFromAlignmentId();
      const toAlignmentId = edge.getToAlignmentId();
      forEach(edge.renderedEdges, rEdge => {
        addToBundle(rEdge, fromAlignmentId);
        addToBundle(rEdge, toAlignmentId);
      });
    });

    const bundleSorter = (a, b) => {
      const aId = a.patternIds || a.pathSegmentIds;
      const bId = b.patternIds || b.pathSegmentIds;
      const aVector = a.getAlignmentVector(this.currentAlignmentId);
      const bVector = b.getAlignmentVector(this.currentAlignmentId);
      const isOutward = isOutwardVector(aVector) && isOutwardVector(bVector) ? 1 : -1;
      const abCompId = aId + '_' + bId;

      if (abCompId in this.bundleComparisons) {
        return isOutward * this.bundleComparisons[abCompId];
      }

      const baCompId = bId + '_' + aId;

      if (baCompId in this.bundleComparisons) {
        return isOutward * this.bundleComparisons[baCompId];
      }

      if (a.route && b.route && a.route.route_type !== b.route.route_type) {
        return a.route.route_type > b.route.route_type ? 1 : -1;
      }

      const isForward = a.forward && b.forward ? 1 : -1;
      return isForward * isOutward * (aId < bId ? -1 : 1);
    };

    forEach(Object.keys(alignmentBundles), alignmentId => {
      const bundleArr = alignmentBundles[alignmentId];
      forEach(bundleArr, bundle => {
        if (bundle.items.length <= 1) return;
        const lw = 1.2;
        const bundleWidth = lw * (bundle.items.length - 1);
        this.currentAlignmentId = alignmentId;
        bundle.items.sort(bundleSorter);
        forEach(bundle.items, (rEdge, i) => {
          const offset = -bundleWidth / 2 + i * lw;

          if (rEdge.getType() === 'TRANSIT') {
            forEach(rEdge.patterns, pattern => {
              pattern.offsetAlignment(alignmentId, offset);
            });
          } else rEdge.offsetAlignment(alignmentId, offset);
        });
      });
    });
  }
  /**
   * Traverses the graph vertex-by-vertex, creating comparisons between all pairs of
   * edges for which a topological relationship can be established.
   */


  initComparisons() {
    this.bundleComparisons = {};
    forEach(this.vertices, vertex => {
      const incidentGraphEdges = vertex.incidentEdges();
      const angleREdges = {};
      forEach(incidentGraphEdges, incidentGraphEdge => {
        const angle = incidentGraphEdge.fromVertex === vertex ? incidentGraphEdge.fromAngle : incidentGraphEdge.toAngle;
        const angleDeg = 180 * angle / Math.PI;
        if (!(angleDeg in angleREdges)) angleREdges[angleDeg] = [];
        angleREdges[angleDeg] = angleREdges[angleDeg].concat(incidentGraphEdge.renderedEdges);
      });
      forEach(angleREdges, rEdges => {
        if (rEdges.length < 2) return;

        for (let i = 0; i < rEdges.length - 1; i++) {
          for (let j = i + 1; j < rEdges.length; j++) {
            const re1 = rEdges[i];
            const re2 = rEdges[j];
            let opp1 = re1.graphEdge.oppositeVertex(vertex);
            let opp2 = re2.graphEdge.oppositeVertex(vertex);
            let isCcw = ccw(opp1.x, opp1.y, vertex.x, vertex.y, opp2.x, opp2.y);

            if (isCcw === 0) {
              const s1Ext = re1.findExtension(opp1);
              const s2Ext = re2.findExtension(opp2);
              if (s1Ext) opp1 = s1Ext.graphEdge.oppositeVertex(opp1);
              if (s2Ext) opp2 = s2Ext.graphEdge.oppositeVertex(opp2);
              isCcw = ccw(opp1.x, opp1.y, vertex.x, vertex.y, opp2.x, opp2.y);
            }

            isCcw = getInverse(re1, re2, vertex) * isCcw;

            if (isCcw > 0) {
              // e1 patterns are 'less' than e2 patterns
              this.storeComparison(re1, re2);
            }

            if (isCcw < 0) {
              // e2 patterns are 'less' than e2 patterns
              this.storeComparison(re2, re1);
            }
          }
        }
      });
    });
  }

  storeComparison(s1, s2) {
    const s1Id = s1.patternIds || s1.pathSegmentIds;
    const s2Id = s2.patternIds || s2.pathSegmentIds;
    debug(`storing comparison: ${s1Id}  < ${s2Id}`);
    this.bundleComparisons[`${s1Id}_${s2Id}`] = -1;
    this.bundleComparisons[`${s2Id}_${s1Id}`] = 1;
  }

}
/**
 *  AlignmentBundle class
 */

class AlignmentBundle {
  constructor() {
    this.items = []; // RenderedEdges

    this.min = Number.MAX_VALUE;
    this.max = -Number.MAX_VALUE;
  }

  addEdge(rEdge, min, max) {
    if (this.items.indexOf(rEdge) === -1) {
      this.items.push(rEdge);
    }

    this.min = Math.min(this.min, min);
    this.max = Math.max(this.max, max);
  }

  rangeOverlaps(min, max) {
    return this.min < max && min < this.max;
  }

}
/** Helper functions **/


function getInverse(s1, s2, vertex) {
  return s1.graphEdge.toVertex === vertex && s2.graphEdge.toVertex === vertex || s1.graphEdge.toVertex === vertex && s2.graphEdge.fromVertex === vertex ? -1 : 1;
}
/**
 * Check if arrays are equal
 */


function equal(a, b) {
  if (a.length !== b.length) {
    return false;
  }

  for (const i in a) {
    if (a[i] !== b[i]) {
      return false;
    }
  }

  return true;
}

/** TODO: use library? **/
function decode(polyline) {
  let currentPosition = 0;
  let currentLat = 0;
  let currentLng = 0;
  const dataLength = polyline.length;
  const polylineLatLngs = [];

  while (currentPosition < dataLength) {
    let shift = 0;
    let result = 0;
    var byte;

    do {
      byte = polyline.charCodeAt(currentPosition++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltaLat = result & 1 ? ~(result >> 1) : result >> 1;
    currentLat += deltaLat;
    shift = 0;
    result = 0;

    do {
      byte = polyline.charCodeAt(currentPosition++) - 63;
      result |= (byte & 0x1f) << shift;
      shift += 5;
    } while (byte >= 0x20);

    const deltLng = result & 1 ? ~(result >> 1) : result >> 1;
    currentLng += deltLng;
    polylineLatLngs.push([currentLat * 0.00001, currentLng * 0.00001]);
  }

  return polylineLatLngs;
}

/**
 * A Point subclass representing a turn point in turn-by-turn directions for a
 * walk/bike/drive segment
 */

class TurnPoint extends Point {
  constructor(data, id) {
    super(data);
    this.name = `Turn @ ${data.lat}, ${data.lon}`;

    if (!this.worldX || !this.worldY) {
      const smCoords = sm.forward([data.lon, data.lat]);
      this.worldX = smCoords[0];
      this.worldY = smCoords[1];
      this.isSegmentEndPoint = false;
    }

    this.id = id;
  }

  getId() {
    return this.id;
  }

  getType() {
    return 'TURN';
  }

  getName() {
    return this.name;
  }

  containsSegmentEndPoint() {
    return this.isSegmentEndPoint;
  }

}

/**
 * LabelEdgeGroup
 */

class LabelEdgeGroup {
  constructor(renderedSegment) {
    this.renderedSegment = renderedSegment;
    this.renderedEdges = [];
  }

  addEdge(rEdge) {
    this.renderedEdges.push(rEdge);
    this.edgeIds = !this.edgeIds ? rEdge.getId() : this.edgeIds + ',' + rEdge.getId();
  }

  getLabelTextArray() {
    const textArray = [];
    forEach(this.renderedSegment.pathSegment.getPatterns(), pattern => {
      const shortName = pattern.route.route_short_name;
      if (textArray.indexOf(shortName) === -1) textArray.push(shortName);
    });
    return textArray;
  }
  /**
   * Find the potential anchors for a label given a display and spacing.
   * @param  {Display} display
   * @param  {Number} spacing - spacing needed for label placement (i.e., label
   *                            height with buffer)
   */


  getLabelAnchors(display, spacing) {
    const labelAnchors = [];
    const renderLen = this.getRenderLength(display); // Determine how many anchors might fit along length.

    const anchorCount = Math.floor(renderLen / spacing);
    const pctSpacing = spacing / renderLen;

    for (let i = 0; i < anchorCount; i++) {
      // Calculate potential position of anchor.
      const t = i % 2 === 0 ? 0.5 + i / 2 * pctSpacing : 0.5 - (i + 1) / 2 * pctSpacing; // Attempt to find coordinate along path for potential anchor.

      const coord = this.coordAlongRenderedPath(t, display);
      if (coord) labelAnchors.push(coord);
    }

    return labelAnchors;
  }
  /**
   * Get the coordinate located at a defined percentage along along a rendered path.
   * @param {Number} t - a value between 0 and 1 representing the location of the
   *   point to be computed
   * @param {Display} display
   * @returns {Object} - the coordinate as an {x,y} Object
   */


  coordAlongRenderedPath(t, display) {
    const renderLen = this.getRenderLength(display); // Get location along path.

    const loc = t * renderLen; // Iterate over each edge in path, accumulating distance as we go.

    let cur = 0;

    for (let i = 0; i < this.renderedEdges.length; i++) {
      const rEdge = this.renderedEdges[i];
      const edgeRenderLen = rEdge.graphEdge.getRenderLength(display);

      if (loc <= cur + edgeRenderLen) {
        // If location is along this edge, find and return the position along
        // the edge.
        const t2 = (loc - cur) / edgeRenderLen;
        const coord = rEdge.graphEdge.coordAlongEdge(t2, rEdge.renderData, display);
        return coord;
      }

      cur += edgeRenderLen;
    }
  }
  /**
   * Get the total render length for the edge group, which consists of the sum
   * of each graph edge length.
   */


  getRenderLength(display) {
    if (!this.renderLength) {
      this.renderLength = 0;
      forEach(this.renderedEdges, rEdge => {
        this.renderLength += rEdge.graphEdge.getRenderLength(display);
      });
    }

    return this.renderLength;
  }

}

/**
 * PatternGroup -- a collection of one or more RoutePatterns associated with
 * a PathSegment
 */
class PatternGroup {
  constructor() {
    this.patterns = []; // lookup tables mapping pattern IDs to their from/to indices in the containing PathSegment

    this.fromIndexLookup = {};
    this.toIndexLookup = {};
  }

  addPattern(pattern, fromIndex, toIndex) {
    if (this.patterns.indexOf(pattern) === -1) {
      this.patterns.push(pattern);
      this.fromIndexLookup[pattern.pattern_id] = fromIndex;
      this.toIndexLookup[pattern.pattern_id] = toIndex;
    }
  }

  getFromIndex(pattern) {
    return this.fromIndexLookup[pattern.pattern_id];
  }

  getToIndex(pattern) {
    return this.toIndexLookup[pattern.pattern_id];
  }

}

/**
 * PathSegment
 */

let segmentId = 0;
class PathSegment {
  constructor(type, path) {
    this.id = segmentId++;
    this.type = type;
    this.path = path;
    this.points = [];
    this.edges = [];
    this.renderedSegments = [];
    this.patternGroup = new PatternGroup();
  }

  clearGraphData() {
    this.edges = [];
    this.points.forEach(function (point) {
      point.graphVertex = null;
    });
    this.renderLength = null;
  }

  getId() {
    return this.id;
  }

  getType() {
    return this.type;
  }

  addRenderedSegment(rSegment) {
    this.renderedSegments.push(rSegment);
  }

  addEdge(graphEdge, originVertex) {
    this.edges.push({
      forward: originVertex === graphEdge.fromVertex,
      graphEdge: graphEdge
    });
  }

  insertEdgeAt(index, graphEdge, originVertex) {
    const edgeInfo = {
      forward: originVertex === graphEdge.fromVertex,
      graphEdge: graphEdge
    };
    this.edges.splice(index, 0, edgeInfo);
  }

  removeEdge(graphEdge) {
    let index = null;

    for (let i = 0; i < this.edges.length; i++) {
      if (this.edges[i].graphEdge === graphEdge) {
        index = i;
        break;
      }
    }

    if (index !== null) this.edges.splice(index, 1);
  }

  getEdgeIndex(graphEdge) {
    for (let i = 0; i < this.edges.length; i++) {
      if (this.edges[i].graphEdge === graphEdge) return i;
    }

    return -1;
  }
  /**
   * Get graph vertices
   */


  getGraphVertices() {
    const vertices = [];
    this.edges.forEach(function (edge, i) {
      if (i === 0) {
        vertices.push(edge.graphEdge.fromVertex);
      }

      vertices.push(edge.graphEdge.toVertex);
    });
    return vertices;
  }

  vertexArray() {
    let vertex = this.startVertex();
    const array = [vertex];
    this.edges.forEach(function (edgeInfo) {
      vertex = edgeInfo.graphEdge.oppositeVertex(vertex);
      array.push(vertex);
    });
    return array;
  }

  startVertex() {
    if (this.points[0].multipoint) return this.points[0].multipoint.graphVertex;
    if (!this.edges || this.edges.length === 0) return null;
    const firstGraphEdge = this.edges[0].graphEdge;
    return this.edges[0].forward ? firstGraphEdge.fromVertex : firstGraphEdge.toVertex;
    /* if (this.graphEdges.length === 1) return this.graphEdges[0].fromVertex;
    var first = this.graphEdges[0],
      next = this.graphEdges[1];
    if (first.toVertex == next.toVertex || first.toVertex == next.fromVertex)
      return first.fromVertex;
    if (first.fromVertex == next.toVertex || first.fromVertex == next.fromVertex)
      return first.toVertex;
    return null; */
  }

  endVertex() {
    if (this.points[this.points.length - 1].multipoint) {
      return this.points[this.points.length - 1].multipoint.graphVertex;
    }

    if (!this.edges || this.edges.length === 0) return null;
    const lastGraphEdge = this.edges[this.edges.length - 1].graphEdge;
    return this.edges[this.edges.length - 1].forward ? lastGraphEdge.toVertex : lastGraphEdge.fromVertex;
    /* if (this.graphEdges.length === 1) return this.graphEdges[0].toVertex;
    var last = this.graphEdges[this.graphEdges.length - 1],
      prev = this.graphEdges[this.graphEdges.length - 2];
    if (last.toVertex == prev.toVertex || last.toVertex == prev.fromVertex)
      return last.fromVertex;
    if (last.fromVertex == prev.toVertex || last.fromVertex == prev.fromVertex)
      return last.toVertex;
    return null; */
  }

  addPattern(pattern, fromIndex, toIndex) {
    // Initialize this segment's 'points' array to include the stops in the
    // provided pattern between the specified from and to indices, inclusive.
    // Only do this if the points array is empty or if the the length of the
    // segment being added exceeds that of the one currently stored.
    if (toIndex - fromIndex + 1 > this.points.length) {
      this.points = [];
      let lastStop = null;

      for (let i = fromIndex; i <= toIndex; i++) {
        const stop = pattern.stops[i];

        if (lastStop !== stop) {
          this.points.push(stop);
        }

        lastStop = stop;
      }
    } // Add the pattern to this segment's PatternGroup


    this.patternGroup.addPattern(pattern, fromIndex, toIndex);
  }

  getPattern() {
    return this.patternGroup.patterns[0];
  }

  getPatterns() {
    return this.patternGroup.patterns;
  }

  getMode() {
    return this.patternGroup.patterns[0].route.route_type;
  }

  toString() {
    const startVertex = this.startVertex();
    const endVertex = this.endVertex();
    return 'PathSegment id=' + this.id + ' type=' + this.type + ' from ' + (startVertex ? startVertex.toString() : '(unknown)') + ' to ' + (endVertex ? endVertex.toString() : '(unknown)');
  }

  getLabelEdgeGroups() {
    const edgeGroups = [];
    forEach(this.renderedSegments, rSegment => {
      if (!rSegment.isFocused()) return;
      let currentGroup = new LabelEdgeGroup(rSegment);
      forEach(rSegment.renderedEdges, rEdge => {
        currentGroup.addEdge(rEdge);

        if (rEdge.graphEdge.toVertex.point.containsSegmentEndPoint()) {
          edgeGroups.push(currentGroup);
          currentGroup = new LabelEdgeGroup(rSegment);
        }
      });
    });
    return edgeGroups;
  }

}

/**
 * A path through the network graph. Composed of PathSegments (which
 * are in turn composed of a sequence of graph edges)
 */
class NetworkPath {
  /**
   * NetworkPath constructor
   * @param {Object} parent the parent object (a RoutePattern or Journey)
   */
  constructor(parent) {
    this.parent = parent;
    this.segments = [];
  }

  clearGraphData(segment) {
    this.segments.forEach(function (segment) {
      segment.clearGraphData();
    });
  }
  /**
   * addSegment: add a new segment to the end of this NetworkPath
   */


  addSegment(segment) {
    this.segments.push(segment);
    segment.points.forEach(function (point) {
      point.paths.push(this);
    }, this);
  }

  getRenderedSegments() {
    let renderedSegments = [];
    this.segments.forEach(function (pathSegment) {
      renderedSegments = renderedSegments.concat(pathSegment.renderedSegments);
    });
    return renderedSegments;
  }
  /**
   * getPointArray
   */


  getPointArray() {
    const points = [];

    for (let i = 0; i < this.segments.length; i++) {
      const segment = this.segments[i];

      if (i > 0 && segment.points[0] === this.segments[i - 1].points[this.segments[i - 1].points.length - 1]) {
        points.concat(segment.points.slice(1));
      }
    }

    return points;
  }

}

/**
 * Journey
 */

class Journey {
  constructor(data, network) {
    this.network = network;

    for (const key in data) {
      this[key] = data[key];
    }

    this.path = new NetworkPath(this);
    forEach(this.segments, segmentInfo => {
      const pathSegment = new PathSegment(segmentInfo.type, this.path);
      pathSegment.journeySegment = segmentInfo;

      if (segmentInfo.type === 'TRANSIT') {
        if (segmentInfo.patterns) {
          forEach(segmentInfo.patterns, patternInfo => {
            pathSegment.addPattern(network.patterns[patternInfo.pattern_id], patternInfo.from_stop_index, patternInfo.to_stop_index);
          });
        } else if (segmentInfo.pattern_id) {
          // legacy support for single-pattern journey segments
          pathSegment.addPattern(network.patterns[segmentInfo.pattern_id], segmentInfo.from_stop_index, segmentInfo.to_stop_index);
        }
      } else {
        // non-transit segment
        const streetEdges = []; // screen out degenerate transfer segments

        if (segmentInfo.from.type === 'STOP' && segmentInfo.to.type === 'STOP' && segmentInfo.from.stop_id === segmentInfo.to.stop_id) return;
        pathSegment.points.push(getEndPoint(segmentInfo.from, network));

        if (segmentInfo.streetEdges && segmentInfo.streetEdges.length > 0) {
          let lastTurnPoint = null;

          for (let i = 0; i < segmentInfo.streetEdges.length; i++) {
            const streetEdgeId = segmentInfo.streetEdges[i];
            const streetEdge = network.streetEdges[streetEdgeId];
            streetEdge.id = streetEdgeId;
            streetEdges.push(streetEdge);
            if (i >= segmentInfo.streetEdges.length - 1) continue;
            if (lastTurnPoint) streetEdge.fromTurnPoint = lastTurnPoint;
            const lastIndex = streetEdge.length - 1; // screen out degenerate edges

            if (streetEdge.latLons[0][0] === streetEdge.latLons[lastIndex][0] && streetEdge.latLons[0][1] === streetEdge.latLons[lastIndex][1]) {
              continue;
            } // create a TurnPoint for the 'from' point of this edge


            const turnPoint = getTurnPoint({
              lat: streetEdge.latLons[lastIndex][0],
              lon: streetEdge.latLons[lastIndex][1],
              worldX: streetEdge.worldCoords[lastIndex][0],
              worldY: streetEdge.worldCoords[lastIndex][1]
            }, network); // compute the angle represented by this turn point

            /* turnPoint.turnAngle = Util.angleFromThreePoints(
              streetEdge.worldCoords[0][0],
              streetEdge.worldCoords[0][1],
              streetEdge.worldCoords[lastIndex][0],
              streetEdge.worldCoords[lastIndex][1],
              nextEdge.worldCoords[nextEdge.length-1][0],
              nextEdge.worldCoords[nextEdge.length-1][1]
            ); */

            pathSegment.points.push(turnPoint);
            lastTurnPoint = streetEdge.toTurnPoint = turnPoint;
          }

          pathSegment.streetEdges = streetEdges;
        }

        pathSegment.points.push(getEndPoint(segmentInfo.to, network));
      }

      this.path.addSegment(pathSegment);
    });
  }

  getElementId() {
    return 'journey-' + this.journey_id;
  }

}

function getEndPoint(pointInfo, network) {
  if (pointInfo.type === 'PLACE') {
    return network.places[pointInfo.place_id];
  } else if (pointInfo.type === 'STOP') {
    return network.stops[pointInfo.stop_id];
  }
}
/* utility function for creating non-duplicative TurnPoints */


function getTurnPoint(turnPointInfo, network) {
  const key = turnPointInfo.lat + '_' + turnPointInfo.lon;
  if (key in network.turnPoints) return network.turnPoints[key];
  const turnPoint = new TurnPoint(turnPointInfo, key);
  network.turnPoints[key] = turnPoint; // network.addVertexPoint(turnPoint);

  return turnPoint;
}

/**
 * A RoutePattern
 */

class RoutePattern {
  /**
   * RoutePattern constructor
   *
   * @param {Object} RoutePattern data object from the transitive.js input
   */
  constructor(data, transitive) {
    for (const key in data) {
      if (key === 'stops') continue;
      this[key] = data[key];
    } // the array of Stops that make up this pattern


    this.stops = []; // the inter-stop geometry, an array of point sequences (themselves arrays)
    // that represent the geometry between stops i and i+1. This array should be
    // exactly one item shorter than the stops array.

    this.interStopGeometry = [];

    if (transitive) {
      forEach(data.stops, stop => {
        // look up the Stop in the master collection and add to the stops array
        this.stops.push(transitive.stops[stop.stop_id]); // if inter-stop geometry is provided: decode polyline, convert points
        // to SphericalMercator, and add to the interStopGeometry array

        if (stop.geometry) {
          const latLons = decode(stop.geometry);
          const coords = [];
          forEach(latLons, latLon => {
            coords.push(sm.forward([latLon[1], latLon[0]]));
          });
          this.interStopGeometry.push(coords);
        }
      });
    }

    this.renderedEdges = [];
  }

  getId() {
    return this.pattern_id;
  }

  getElementId() {
    return 'pattern-' + this.pattern_id;
  }

  getName() {
    return this.pattern_name;
  }

  addRenderedEdge(rEdge) {
    if (this.renderedEdges.indexOf(rEdge) === -1) this.renderedEdges.push(rEdge);
  }

  offsetAlignment(alignmentId, offset) {
    forEach(this.renderedEdges, rEdge => {
      rEdge.offsetAlignment(alignmentId, offset);
    });
  }

  createPath() {
    const path = new NetworkPath(this);
    const pathSegment = new PathSegment('TRANSIT', path);
    pathSegment.addPattern(this, 0, this.stops.length - 1);
    path.addSegment(pathSegment);
    return path;
  }

}

/**
 * For a provided hex color, returns an inverted color for legibility.
 * @param  {[type]} hex - hexadecimal (HTML) color, e.g. #FFFFFF
 * @param  {[type]} bw  - whether to return only black or white as inverted color
 * @return {[type]}     [description]
 */
function invertColor(hex, bw = true) {
  if (hex.indexOf('#') === 0) {
    hex = hex.slice(1);
  } // convert 3-digit hex to 6-digits.


  if (hex.length === 3) {
    hex = hex[0] + hex[0] + hex[1] + hex[1] + hex[2] + hex[2];
  }

  if (hex.length !== 6) {
    console.warn('Invalid HEX color.', hex);
  }

  let r = parseInt(hex.slice(0, 2), 16);
  let g = parseInt(hex.slice(2, 4), 16);
  let b = parseInt(hex.slice(4, 6), 16);

  if (bw) {
    // http://stackoverflow.com/a/3943023/112731
    return r * 0.299 + g * 0.587 + b * 0.114 > 186 ? '#000000' : '#FFFFFF';
  } // invert color components


  r = (255 - r).toString(16);
  g = (255 - g).toString(16);
  b = (255 - b).toString(16); // pad each with zeros and return

  return '#' + padZero(r) + padZero(g) + padZero(b);
}
function padZero(str, len) {
  len = len || 2;
  const zeros = new Array(len).join('0');
  return (zeros + str).slice(-len);
}

/**
 * A transit Route, as defined in the input data.
 * Routes contain one or more Patterns.
 */

class Route {
  constructor(data) {
    for (const key in data) {
      if (key === 'patterns') continue;
      this[key] = data[key];
    }

    this.patterns = [];
  }
  /**
   * Add Pattern
   *
   * @param {Pattern}
   */


  addPattern(pattern) {
    this.patterns.push(pattern);
    pattern.route = this;
  }
  /**
   * Rather than rely on the route text color field to be defined, simply return
   * the black or white inverse of the background color.
   */


  getTextColor() {
    const bgColor = this.getColor();
    if (bgColor) return invertColor(bgColor);
  }

  getColor() {
    if (this.route_color) {
      if (this.route_color.charAt(0) === '#') return this.route_color;
      return '#' + this.route_color;
    } // assign a random shade of gray

    /* var c = 128 + Math.floor(64 * Math.random());
    var hex = c.toString(16);
    hex = (hex.length === 1) ? '0' + hex : hex;
     this.route_color = '#' + hex + hex + hex;
     return this.route_color; */

  }

}

const debug$1 = /*#__PURE__*/require('debug')('transitive:network');
/**
 * Network
 */


class Network {
  constructor(transitive, data) {
    this.transitive = transitive;
    this.routes = {};
    this.stops = {};
    this.patterns = {};
    this.places = {};
    this.journeys = {};
    this.paths = [];
    this.baseVertexPoints = [];
    this.graph = new NetworkGraph(this, []);
    if (data) this.load(data);
  }
  /**
   * Load
   *
   * @param {Object} data
   */


  load(data) {
    debug$1('loading', data); // check data

    if (!data) data = {}; // Store data

    this.data = data; // A list of points (stops & places) that will always become vertices in the network
    // graph (regardless of zoom scale). This includes all points that serve as a segment
    // endpoint and/or a convergence/divergence point between segments

    this.baseVertexPoints = []; // object maps stop ids to arrays of unique stop_ids reachable from that stop

    this.adjacentStops = {}; // maps lat_lon key to unique TurnPoint object

    this.turnPoints = {}; // Copy/decode the streetEdge objects

    this.streetEdges = {};
    forEach(data.streetEdges, streetEdgeData => {
      const latLons = decode(streetEdgeData.geometry.points);
      const coords = [];
      forEach(latLons, latLon => {
        coords.push(sm.forward([latLon[1], latLon[0]]));
      });
      this.streetEdges[streetEdgeData.edge_id] = {
        latLons: latLons,
        length: streetEdgeData.geometry.length,
        worldCoords: coords
      };
    }); // Generate the route objects

    this.routes = {};
    forEach(data.routes, routeData => {
      this.routes[routeData.route_id] = new Route(routeData);
    }); // Generate the stop objects

    this.stops = {};
    forEach(data.stops, stopData => {
      this.stops[stopData.stop_id] = new Stop(stopData);
    }); // Generate the pattern objects

    this.patterns = {};
    forEach(data.patterns, patternData => {
      const pattern = new RoutePattern(patternData, this);
      this.patterns[patternData.pattern_id] = pattern;
      const route = this.routes[patternData.route_id];

      if (route) {
        route.addPattern(pattern);
        pattern.route = route;
      } else {
        debug$1('Error: pattern ' + patternData.pattern_id + ' refers to route that was not found: ' + patternData.route_id);
      }

      if (pattern.render) this.paths.push(pattern.createPath());
    }); // Generate the place objects

    this.places = {};
    forEach(data.places, placeData => {
      const place = this.places[placeData.place_id] = new Place(placeData, this);
      this.addVertexPoint(place);
    }); // Generate the internal Journey objects

    this.journeys = {};
    forEach(data.journeys, journeyData => {
      const journey = new Journey(journeyData, this);
      this.journeys[journeyData.journey_id] = journey;
      this.paths.push(journey.path);
    }); // process the path segments

    for (let p = 0; p < this.paths.length; p++) {
      const path = this.paths[p];

      for (let s = 0; s < path.segments.length; s++) {
        this.processSegment(path.segments[s]);
      }
    } // when rendering pattern paths only, determine convergence/divergence vertex
    // stops by looking for stops w/ >2 adjacent stops


    if (!data.journeys || data.journeys.length === 0) {
      for (const stopId in this.adjacentStops) {
        if (this.adjacentStops[stopId].length > 2) {
          this.addVertexPoint(this.stops[stopId]);
        }
      }
    } // determine which TurnPoints should be base vertices


    const turnLookup = {};

    const addTurn = function (turn1, turn2) {
      if (!(turn1.getId() in turnLookup)) turnLookup[turn1.getId()] = [];

      if (turnLookup[turn1.getId()].indexOf(turn2) === -1) {
        turnLookup[turn1.getId()].push(turn2);
      }
    };

    forEach(Object.values(this.streetEdges), streetEdge => {
      if (streetEdge.fromTurnPoint && streetEdge.toTurnPoint) {
        addTurn(streetEdge.toTurnPoint, streetEdge.fromTurnPoint);
        addTurn(streetEdge.fromTurnPoint, streetEdge.toTurnPoint);
      }
    });

    for (const turnPointId in turnLookup) {
      const count = turnLookup[turnPointId].length;
      if (count > 2) this.addVertexPoint(this.turnPoints[turnPointId]);
    }

    this.createGraph();
    this.loaded = true;
    this.emit('load', this);
    return this;
  }
  /** Graph Creation/Processing Methods **/


  clearGraphData() {
    forEach(this.paths, path => {
      path.clearGraphData();
    });
  }

  createGraph() {
    this.applyZoomFactors(this.transitive.display.activeZoomFactors); // clear previous graph-specific data

    if (this.pointClusterMap) this.pointClusterMap.clearMultiPoints();
    forEach(Object.values(this.stops), stop => {
      stop.setFocused(true);
    }); // create the list of vertex points

    let vertexPoints;

    if (this.mergeVertexThreshold && this.mergeVertexThreshold > 0) {
      this.pointClusterMap = new PointClusterMap(this, this.mergeVertexThreshold);
      vertexPoints = this.pointClusterMap.getVertexPoints(this.baseVertexPoints);
    } else vertexPoints = this.baseVertexPoints; // core graph creation steps


    this.graph = new NetworkGraph(this, vertexPoints);
    this.populateGraphEdges();
    this.graph.pruneVertices();
    this.createInternalVertexPoints();
    if (this.isSnapping()) this.graph.snapToGrid(this.gridCellSize);
    this.graph.sortVertices(); // other post-processing actions

    this.annotateTransitPoints(); // this.initPlaceAdjacency();

    this.createRenderedSegments();
    this.transitive.labeler.updateLabelList(this.graph);
    this.updateGeometry(true);
  }

  isSnapping() {
    return this.gridCellSize && this.gridCellSize !== 0;
  }
  /*
   * identify and populate the 'internal' vertex points, which is zoom-level specfic
   */


  createInternalVertexPoints() {
    this.internalVertexPoints = [];

    for (const i in this.graph.edgeGroups) {
      const edgeGroup = this.graph.edgeGroups[i];
      const wlen = edgeGroup.getWorldLength();
      const splitPoints = []; // compute the maximum number of internal points for this edge to add as graph vertices

      if (edgeGroup.hasTransit()) {
        const vertexFactor = this.internalVertexFactor; //! edgeGroup.hasTransit() ? 1 : this.internalVertexFactor;

        const newVertexCount = Math.floor(wlen / vertexFactor); // get the priority queue of the edge's internal points

        const pq = edgeGroup.getInternalVertexPQ(); // pull the 'best' points from the queue until we reach the maximum

        while (splitPoints.length < newVertexCount && pq.size() > 0) {
          const el = pq.deq();
          splitPoints.push(el.point);
        }
      } // perform the split operation (if needed)


      if (splitPoints.length > 0) {
        for (let e = 0; e < edgeGroup.edges.length; e++) {
          const edge = edgeGroup.edges[e];
          this.graph.splitEdgeAtInternalPoints(edge, splitPoints);
        }
      } else if (edgeGroup.hasTransit()) {
        // special case: transit edge with no internal vertices (i.e. intermediate stops)
        edgeGroup.edges.forEach(edge => {
          if (edge.pointGeom && edge.pointGeom.length > 0) {
            edge.geomCoords = edge.pointGeom[0].slice(0);
          }
        });
      }
    }
  }

  updateGeometry() {
    // clear the stop render data
    // for (var key in this.stops) this.stops[key].renderData = [];
    this.graph.vertices.forEach(function (vertex) {
      // vertex.snapped = false;
      vertex.point.clearRenderData();
    }); // refresh the edge-based points

    this.graph.edges.forEach(function (edge) {
      edge.pointArray.forEach(function (point) {
        point.clearRenderData();
      });
    });
    this.renderedEdges.forEach(function (rEdge) {
      rEdge.clearOffsets();
    }); // if (snapGrid)
    // if(this.gridCellSize && this.gridCellSize !== 0) this.graph.snapToGrid(this.gridCellSize);
    // this.fixPointOverlaps();

    this.graph.calculateGeometry(this.gridCellSize, this.angleConstraint);
    this.graph.apply2DOffsets(this);
  }

  applyZoomFactors(factors) {
    this.gridCellSize = factors.gridCellSize;
    this.internalVertexFactor = factors.internalVertexFactor;
    this.angleConstraint = factors.angleConstraint;
    this.mergeVertexThreshold = factors.mergeVertexThreshold;
    this.useGeographicRendering = factors.useGeographicRendering;
  }
  /**
   *
   */


  processSegment(segment) {
    // iterate through this pattern's stops, associating stops/patterns with
    // each other and initializing the adjacentStops table
    let previousStop = null;

    for (let i = 0; i < segment.points.length; i++) {
      const point = segment.points[i];
      point.used = true; // called for each pair of adjacent stops in sequence

      if (previousStop && point.getType() === 'STOP') {
        this.addStopAdjacency(point.getId(), previousStop.getId());
        this.addStopAdjacency(previousStop.getId(), point.getId());
      }

      previousStop = point.getType() === 'STOP' ? point : null; // add the start and end points to the vertexStops collection

      const startPoint = segment.points[0];
      this.addVertexPoint(startPoint);
      startPoint.isSegmentEndPoint = true;
      const endPoint = segment.points[segment.points.length - 1];
      this.addVertexPoint(endPoint);
      endPoint.isSegmentEndPoint = true;
    }
  }
  /**
   * Helper function for stopAjacency table
   *
   * @param {Stop} adjacent stops list
   * @param {Stop} stopA
   * @param {Stop} stopB
   */


  addStopAdjacency(stopIdA, stopIdB) {
    if (!this.adjacentStops[stopIdA]) this.adjacentStops[stopIdA] = [];

    if (this.adjacentStops[stopIdA].indexOf(stopIdB) === -1) {
      this.adjacentStops[stopIdA].push(stopIdB);
    }
  }
  /**
   * Populate the graph edges
   */


  populateGraphEdges() {
    // vertex associated with the last vertex point we passed in this sequence
    let lastVertex = null; // collection of 'internal' (i.e. non-vertex) points passed
    // since the last vertex point

    let internalPoints = [];
    forEach(this.paths, path => {
      forEach(path.segments, segment => {
        lastVertex = null;
        let streetEdgeIndex = 0; // for transit segments, see if there is a pattern with inter-stop geometry defined

        let representativePattern = null;

        if (segment.type === 'TRANSIT') {
          for (let i = 0; i < segment.patternGroup.patterns.length; i++) {
            const pattern = segment.patternGroup.patterns[i];

            if (pattern.interStopGeometry && pattern.interStopGeometry.length === pattern.stops.length - 1) {
              representativePattern = pattern;
              break;
            }
          }
        }
        /**
         *  geomCoords: The geographic coordinates for the graph edge currently
         *  being constructed, used when rendering edges in "real-world" (i.e.
         *  non-schematic) mode. geomCoords data is only initialized here for
         *  street-based segments, using the segment's embedded street geometry
         *  data (if provided).
         */


        let geomCoords = [];
        /**
         *  pointGeom: An array of point-specific geometry (i.e. the alignment
         *  connecting this point to the following point in the containing
         *  segment's point sequence. Currently applies to transit segments only.
         *  pointGeom data is converted to geomCoords for rendering in the
         *  splitEdgeAtInternalPoints method of NetworkGraph
         */

        let pointGeom = [];
        forEach(segment.points, (point, index) => {
          if (segment.streetEdges) {
            // street-based segment with street-edge geometry
            for (let i = streetEdgeIndex; i < segment.streetEdges.length; i++) {
              if (index === 0) break;
              geomCoords = geomCoords.concat(geomCoords.length > 0 ? segment.streetEdges[i].worldCoords.slice(1) : segment.streetEdges[i].worldCoords);

              if (segment.streetEdges[i].toTurnPoint === point) {
                streetEdgeIndex = i + 1;
                break;
              }
            }
          } else if (representativePattern) {
            // transit-based segment with known geometry
            const fromIndex = segment.patternGroup.getFromIndex(representativePattern); // ignore the first stop, since the geometry at this index represents
            // the alignment leading into that stop

            if (index > 0) {
              // add the alignment extending from this stop to the pointGeom array
              const geom = representativePattern.interStopGeometry[fromIndex + index - 1];
              pointGeom.push(geom);
            }
          }

          if (point.multipoint) point = point.multipoint;

          if (point.graphVertex) {
            // this is a vertex point
            if (lastVertex !== null) {
              if (lastVertex.point === point) return; // see if an equivalent graph edge already exists

              const fromVertex = lastVertex;
              const toVertex = point.graphVertex;
              let edge = this.graph.getEquivalentEdge(internalPoints, fromVertex, toVertex); // create a new graph edge if necessary

              if (!edge) {
                edge = this.graph.addEdge(internalPoints, fromVertex, toVertex, segment.getType());

                if (geomCoords && geomCoords.length > 0) {
                  edge.geomCoords = geomCoords;
                }

                if (pointGeom && pointGeom.length > 0) {
                  edge.pointGeom = pointGeom;
                }
              } // associate the graph edge and path segment with each other


              segment.addEdge(edge, fromVertex);
              edge.addPathSegment(segment); // reset the geomCoords and pointGeom arrays for the next edge

              geomCoords = [];
              pointGeom = [];
            }

            lastVertex = point.graphVertex;
            internalPoints = [];
          } else {
            // this is an internal point
            internalPoints.push(point);
          }
        });
      });
    });
  }

  createGraphEdge(segment, fromVertex, toVertex, internalPoints, geomCoords) {
    let edge = this.graph.getEquivalentEdge(internalPoints, fromVertex, toVertex);

    if (!edge) {
      edge = this.graph.addEdge(internalPoints, fromVertex, toVertex, segment.getType()); // calculate the angle and apply to edge stops

      /* var dx = fromVertex.x - toVertex.x;
      var dy = fromVertex.y - toVertex.y;
      var angle = Math.atan2(dy, dx) * 180 / Math.PI;
      point.angle = lastVertex.point.angle = angle;
      for (var is = 0; is < internalPoints.length; is++) {
        internalPoints[is].angle = angle;
      } */

      if (geomCoords) edge.geomCoords = geomCoords;
      debug$1('--- created edge ' + edge.toString());
    }

    segment.addEdge(edge, fromVertex);
    edge.addPathSegment(segment);
  }

  annotateTransitPoints() {
    this.paths.forEach(function (path) {
      const transitSegments = [];
      path.segments.forEach(function (pathSegment) {
        if (pathSegment.type === 'TRANSIT') transitSegments.push(pathSegment);
      });
      path.segments.forEach(function (pathSegment) {
        if (pathSegment.type === 'TRANSIT') {
          // if first transit segment in path, mark 'from' endpoint as board point
          if (transitSegments.indexOf(pathSegment) === 0) {
            pathSegment.points[0].isBoardPoint = true; // if there are additional transit segments, mark the 'to' endpoint as a transfer point

            if (transitSegments.length > 1) {
              pathSegment.points[pathSegment.points.length - 1].isTransferPoint = true;
            } // if last transit segment in path, mark 'to' endpoint as alight point

          } else if (transitSegments.indexOf(pathSegment) === transitSegments.length - 1) {
            pathSegment.points[pathSegment.points.length - 1].isAlightPoint = true; // if there are additional transit segments, mark the 'from' endpoint as a transfer point

            if (transitSegments.length > 1) {
              pathSegment.points[0].isTransferPoint = true;
            } // if this is an 'internal' transit segment, mark both endpoints as transfer points

          } else if (transitSegments.length > 2) {
            pathSegment.points[0].isTransferPoint = true;
            pathSegment.points[pathSegment.points.length - 1].isTransferPoint = true;
          }
        }
      });
    });
  }

  initPlaceAdjacency() {
    forEach(Object.values(this.places), place => {
      if (!place.graphVertex) return;
      forEach(place.graphVertex.incidentEdges(), edge => {
        const oppVertex = edge.oppositeVertex(place.graphVertex);

        if (oppVertex.point) {
          oppVertex.point.adjacentPlace = place;
        }
      });
    });
  }

  createRenderedSegments() {
    this.reLookup = {};
    this.renderedEdges = [];
    this.renderedSegments = [];

    for (const patternId in this.patterns) {
      this.patterns[patternId].renderedEdges = [];
    }

    forEach(this.paths, path => {
      forEach(path.segments, pathSegment => {
        pathSegment.renderedSegments = [];

        if (pathSegment.type === 'TRANSIT') {
          // create a RenderedSegment for each pattern, except for buses which are collapsed to a single segment
          const busPatterns = [];
          forEach(pathSegment.getPatterns(), pattern => {
            if (pattern.route.route_type === 3) busPatterns.push(pattern);else this.createRenderedSegment(pathSegment, [pattern]);
          });

          if (busPatterns.length > 0) {
            this.createRenderedSegment(pathSegment, busPatterns);
          }
        } else {
          // non-transit segments
          this.createRenderedSegment(pathSegment);
        }
      });
    });
    this.renderedEdges.sort(function (a, b) {
      // process render transit segments before walk
      if (a.getType() === 'WALK') return 1;
      if (b.getType() === 'WALK') return -1;
    });
  }

  createRenderedSegment(pathSegment, patterns) {
    const rSegment = new RenderedSegment(pathSegment);
    forEach(pathSegment.edges, edge => {
      const rEdge = this.createRenderedEdge(pathSegment, edge.graphEdge, edge.forward, patterns);
      rSegment.addRenderedEdge(rEdge);
    });

    if (patterns) {
      rSegment.patterns = patterns;
      rSegment.mode = patterns[0].route.route_type;
    }

    pathSegment.addRenderedSegment(rSegment);
  }

  createRenderedEdge(pathSegment, gEdge, forward, patterns) {
    let rEdge; // construct the edge key, disregarding mode qualifiers (e.g. "_RENT")

    const type = pathSegment.getType().split('_')[0];
    let key = gEdge.id + (forward ? 'F' : 'R') + '_' + type; // for non-bus transit edges, append an exemplar pattern ID to the key

    if (patterns && patterns[0].route.route_type !== 3) {
      key += '_' + patterns[0].getId();
    } // see if this r-edge already exists


    if (key in this.reLookup) {
      rEdge = this.reLookup[key];
    } else {
      // if not, create it
      rEdge = new RenderedEdge(gEdge, forward, type, this.useGeographicRendering);

      if (patterns) {
        forEach(patterns, pattern => {
          pattern.addRenderedEdge(rEdge);
          rEdge.addPattern(pattern);
        });
        rEdge.mode = patterns[0].route.route_type;
      }

      rEdge.points.push(gEdge.fromVertex.point);
      rEdge.points.push(gEdge.toVertex.point);
      gEdge.addRenderedEdge(rEdge);
      rEdge.addPathSegment(pathSegment);
      this.renderedEdges.push(rEdge);
      this.reLookup[key] = rEdge;
    }

    return rEdge;
  }

  addVertexPoint(point) {
    if (this.baseVertexPoints.indexOf(point) !== -1) return;
    this.baseVertexPoints.push(point);
  }

}
/**
 * Mixin `Emitter`
 */

Emitter(Network.prototype);

class LinearScale {
  constructor(domain, range) {
    this.domain = domain;
    this.range = range;
  }

  compute(val) {
    const {
      domain: d,
      range: r
    } = this;
    return (val - d[0]) / (d[1] - d[0]) * (r[1] - r[0]) + r[0];
  }

  invert(val) {
    const {
      domain: d,
      range: r
    } = this;
    return (val - r[0]) / (r[1] - r[0]) * (d[1] - d[0]) + d[0];
  }

}

class Display$1 {
  constructor(transitive) {
    this.transitive = transitive;
    this.zoomFactors = transitive.options.zoomFactors || this.getDefaultZoomFactors();
    this.updateActiveZoomFactors(1);
  }

  setDimensions(width, height) {
    this.width = width;
    this.height = height;
  }

  setXDomain(domain) {
    // [minX , maxX]
    this.xDomain = domain;
    this.xScale = new LinearScale(domain, [0, this.width]);

    if (!this.initialXDomain) {
      this.initialXDomain = domain;
      this.initialXRes = (domain[1] - domain[0]) / this.width;
    }
  }

  setYDomain(domain) {
    // [minY , maxY]
    this.yDomain = domain;
    this.yScale = new LinearScale(domain, [this.height, 0]);
    if (!this.initialYDomain) this.initialYDomain = domain;
  }

  fitToWorldBounds(bounds) {
    const domains = this.computeDomainsFromBounds(bounds);
    this.setXDomain(domains[0]);
    this.setYDomain(domains[1]);
    this.computeScale();
  }

  reset() {
    this.initialXDomain = null;
    this.initialYDomain = null;
    this.scaleSet = false;
    this.lastScale = undefined;
  }
  /**
   * Apply a transformation {x, y, k} to the *initial* state of the map, where
   * (x, y) is the pixel offset and k is a scale factor relative to an initial
   * zoom level of 1.0. Intended primarily to support D3-style panning/zooming.
   */


  applyTransform(transform) {
    const {
      k,
      x,
      y
    } = transform;
    let xMin = this.initialXDomain[0];
    let xMax = this.initialXDomain[1];
    let yMin = this.initialYDomain[0];
    let yMax = this.initialYDomain[1]; // Apply the scale factor

    xMax = xMin + (xMax - xMin) / k;
    yMin = yMax - (yMax - yMin) / k; // Apply the translation

    const xOffset = -x * (xMax - xMin) / this.width;
    xMin += xOffset;
    xMax += xOffset;
    const yOffset = y * (yMax - yMin) / this.height;
    yMin += yOffset;
    yMax += yOffset; // Update the scale functions and recompute the internal scale factor

    this.setXDomain([xMin, xMax]);
    this.setYDomain([yMin, yMax]);
    this.computeScale();
  }

  getDefaultZoomFactors(data) {
    return [{
      angleConstraint: 45,
      gridCellSize: 25,
      internalVertexFactor: 1000000,
      mergeVertexThreshold: 200,
      minScale: 0
    }, {
      angleConstraint: 5,
      gridCellSize: 0,
      internalVertexFactor: 0,
      mergeVertexThreshold: 0,
      minScale: 1.5
    }];
  }

  updateActiveZoomFactors(scale) {
    let updated = false;

    for (let i = 0; i < this.zoomFactors.length; i++) {
      const min = this.zoomFactors[i].minScale;
      const max = i < this.zoomFactors.length - 1 ? this.zoomFactors[i + 1].minScale : Number.MAX_VALUE; // check if we've crossed into a new zoomFactor partition

      if ((!this.lastScale || this.lastScale < min || this.lastScale >= max) && scale >= min && scale < max) {
        this.activeZoomFactors = this.zoomFactors[i];
        updated = true;
      }
    }

    return updated;
  }

  computeScale() {
    this.lastScale = this.scale;
    this.scaleSet = true;
    const newXRes = (this.xDomain[1] - this.xDomain[0]) / this.width;
    this.scale = this.initialXRes / newXRes;
    if (this.lastScale !== this.scale) this.scaleChanged();
  }

  scaleChanged() {
    const zoomFactorsChanged = this.updateActiveZoomFactors(this.scale);

    if (zoomFactorsChanged) {
      this.transitive.network = null;
      this.transitive.render();
    }
  }
  /**
   * Compute the x/y coordinate space domains to fit the graph.
   */


  computeDomainsFromBounds(bounds) {
    const xmin = bounds[0][0];
    const xmax = bounds[1][0];
    const ymin = bounds[0][1];
    const ymax = bounds[1][1];
    const xRange = xmax - xmin;
    const yRange = ymax - ymin;
    const {
      options
    } = this.transitive;
    const paddingFactor = options && options.paddingFactor ? options.paddingFactor : 0.1;
    const margins = this.getMargins();
    const usableHeight = this.height - margins.top - margins.bottom;
    const usableWidth = this.width - margins.left - margins.right;
    const displayAspect = this.width / this.height;
    const usableDisplayAspect = usableWidth / usableHeight;
    const graphAspect = xRange / (yRange === 0 ? -Infinity : yRange);
    let padding;
    let dispX1, dispX2, dispY1, dispY2;
    let dispXRange, dispYRange;

    if (usableDisplayAspect > graphAspect) {
      // y-axis is limiting
      padding = paddingFactor * yRange;
      dispY1 = ymin - padding;
      dispY2 = ymax + padding;
      dispYRange = yRange + 2 * padding;
      const addedYRange = this.height / usableHeight * dispYRange - dispYRange;

      if (margins.top > 0 || margins.bottom > 0) {
        dispY1 -= margins.bottom / (margins.bottom + margins.top) * addedYRange;
        dispY2 += margins.top / (margins.bottom + margins.top) * addedYRange;
      }

      dispXRange = (dispY2 - dispY1) * displayAspect;
      const xOffset = (margins.left - margins.right) / this.width;
      const xMidpoint = (xmax + xmin - dispXRange * xOffset) / 2;
      dispX1 = xMidpoint - dispXRange / 2;
      dispX2 = xMidpoint + dispXRange / 2;
    } else {
      // x-axis limiting
      padding = paddingFactor * xRange;
      dispX1 = xmin - padding;
      dispX2 = xmax + padding;
      dispXRange = xRange + 2 * padding;
      const addedXRange = this.width / usableWidth * dispXRange - dispXRange;

      if (margins.left > 0 || margins.right > 0) {
        dispX1 -= margins.left / (margins.left + margins.right) * addedXRange;
        dispX2 += margins.right / (margins.left + margins.right) * addedXRange;
      }

      dispYRange = (dispX2 - dispX1) / displayAspect;
      const yOffset = (margins.bottom - margins.top) / this.height;
      const yMidpoint = (ymax + ymin - dispYRange * yOffset) / 2;
      dispY1 = yMidpoint - dispYRange / 2;
      dispY2 = yMidpoint + dispYRange / 2;
    }

    return [[dispX1, dispX2], [dispY1, dispY2]];
  }

  getMargins() {
    return Object.assign({
      bottom: 0,
      left: 0,
      right: 0,
      top: 0
    }, this.transitive.options.displayMargins);
  }

  isInRange(x, y) {
    return x >= 0 && x <= this.width && y >= 0 && y <= this.height;
  }
  /** Methods to be defined by subclasses **/


  clear() {
    throw new Error('method not implemented by subclass!');
  }

  drawCircle(coord, attrs) {
    throw new Error('method not implemented by subclass!');
  }

  drawEllipse(coord, attrs) {
    throw new Error('method not implemented by subclass!');
  }

  drawRect(upperLeft, attrs) {
    throw new Error('method not implemented by subclass!');
  }

  drawText(text, anchor, attrs) {
    throw new Error('method not implemented by subclass!');
  }

  drawPath(renderData, attrs) {
    throw new Error('method not implemented by subclass!');
  }

}

class SvgDisplay extends Display$1 {
  constructor(transitive) {
    super(transitive);
    const {
      el
    } = transitive.options; // TODO: handle case of externally-provided SVG?
    // We have a DOM element

    if (el) {
      this.setDimensions(el.clientWidth, el.clientHeight);
      this.svg = SVG(el);
    }
  }

  clear() {
    this.svg.clear();
  }

  drawRect(upperLeft, attrs) {
    this.svg.rect().move(upperLeft.x, upperLeft.y).attr(attrs);
  }

  drawCircle(center, attrs) {
    this.svg.circle().move(center.x, center.y).attr(attrs);
  }

  drawEllipse(center, attrs) {
    this.svg.ellipse().move(center.x, center.y).attr(attrs);
  }

  drawPath(renderData, attrs) {
    this.svg.path(renderDataToSvgPath(renderData)).attr(attrs);
  }

  drawText(text, anchor, attrs) {
    this.svg.text(text).move(anchor.x, anchor.y).attr(attrs);
  }

}

class CanvasDisplay extends Display$1 {
  constructor(transitive) {
    super(transitive);
    const {
      canvas,
      el
    } = transitive.options; // Handle case of externally-provided canvas

    if (canvas) {
      // Set internal dimensions to match those of canvas
      this.setDimensions(canvas.width, canvas.height);
      this.setCanvas(canvas); // We have a DOM element; create canvas
    } else if (el) {
      this.setDimensions(el.clientWidth, el.clientHeight);
      const canvas = document.createElement('canvas');
      canvas.width = el.clientWidth;
      canvas.height = el.clientHeight;
      el.appendChild(canvas); // Check for Hi-PPI display

      if (window.devicePixelRatio > 1) makeCanvasHiPPI(canvas);
      this.setCanvas(canvas);
    }
  }

  setCanvas(canvas) {
    this.canvas = canvas;
    this.ctx = this.canvas.getContext('2d');
  }

  clear() {
    this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
  }

  drawRect(upperLeft, attrs) {
    this.ctx.strokeStyle = attrs.stroke;
    this.ctx.lineWidth = attrs['stroke-width'];
    this.ctx.fillStyle = attrs.fill;
    this.ctx.beginPath();

    if (attrs.rx && attrs.ry && attrs.rx === attrs.ry) {
      roundedRect(this.ctx, upperLeft.x, upperLeft.y, attrs.width, attrs.height, attrs.rx); // TODO: handle case where rx != ry
    } else {
      // ordinary rectangle
      this.ctx.rect(upperLeft.x, upperLeft.y, attrs.width, attrs.height);
    }

    this.ctx.closePath();
    if (attrs.fill) this.ctx.fill();
    if (attrs.stroke) this.ctx.stroke();
  }

  drawCircle(center, attrs) {
    this.ctx.beginPath();
    this.ctx.arc(center.x, center.y, attrs.r, 0, Math.PI * 2, true);
    this.ctx.closePath();

    if (attrs.fill) {
      this.ctx.fillStyle = attrs.fill;
      this.ctx.fill();
    }

    if (attrs.stroke) {
      this.ctx.strokeStyle = attrs.stroke;
      this.ctx.lineWidth = attrs['stroke-width'] || 1;
      this.ctx.stroke();
    }
  }

  drawEllipse(center, attrs) {// TODO: implement
  }

  drawPath(renderData, attrs) {
    if (!renderData || renderData.length === 0) return; // Apply stroke/width styles

    this.ctx.strokeStyle = attrs.stroke;
    this.ctx.lineWidth = attrs['stroke-width']; // Apply line-dash style, if present

    if (attrs['stroke-dasharray']) {
      const arr = attrs['stroke-dasharray'].split(',').map(str => parseFloat(str.trim()));
      this.ctx.setLineDash(arr);
    } // Apply linecap style


    this.ctx.lineCap = attrs['stroke-linecap'] || 'butt'; // Apply 'round' line join for smooth edge rendering.
    // (The default join is 'miter', which causes jagged edges when zoomed away.)

    this.ctx.lineJoin = 'round'; // Draw the path

    this.ctx.beginPath();
    this.ctx.moveTo(renderData[0].x, renderData[0].y);

    for (let i = 1; i < renderData.length; i++) {
      const rd = renderData[i];
      if (rd.arc) this.ctx.arcTo(rd.ex, rd.ey, rd.x, rd.y, rd.radius);else this.ctx.lineTo(rd.x, rd.y);
    }

    this.ctx.stroke(); // Revert line-dash style to default

    if (attrs['stroke-dasharray']) this.ctx.setLineDash([]);
  }

  drawText(text, anchor, attrs) {
    // For equivalence w/ SVG text rendering
    this.ctx.textBaseline = 'top';
    this.ctx.font = `${attrs['font-size'] || '14px'} ${attrs['font-family'] || 'sans-serif'}`;
    if (attrs['text-anchor']) this.ctx.textAlign = attrs['text-anchor'];

    if (attrs.stroke) {
      this.ctx.strokeStyle = attrs.stroke;

      if (attrs['stroke-opacity']) {
        this.ctx.globalAlpha = attrs['stroke-opacity'];
      }

      this.ctx.lineWidth = attrs['stroke-width'] || 1;
      this.ctx.strokeText(text, anchor.x, anchor.y);
    }

    if (attrs.fill) {
      this.ctx.fillStyle = attrs.fill;
      if (attrs['fill-opacity']) this.ctx.globalAlpha = attrs['fill-opacity'];
      this.ctx.fillText(text, anchor.x, anchor.y);
    }

    this.ctx.textAlign = 'start';
    this.ctx.globalAlpha = 1;
  }

} // Utility function to support HiPPI displays (e.g. Retina)

function makeCanvasHiPPI(canvas) {
  const PIXEL_RATIO = 2;
  canvas.style.width = canvas.width + 'px';
  canvas.style.height = canvas.height + 'px';
  canvas.width *= PIXEL_RATIO;
  canvas.height *= PIXEL_RATIO;
  const context = canvas.getContext('2d');
  context.scale(PIXEL_RATIO, PIXEL_RATIO);
}

/**
 * A superclass for a Transitive network rendering engine.
 */

class Renderer {
  /**
   * Renderer constructor
   * @param {Object} transitive the main Transitive object
   */
  constructor(transitive) {
    this.transitive = transitive;
  }

  render() {
    const display = this.transitive.display;
    const graph = this.transitive.network.graph;
    display.styler = this.transitive.styler;
    graph.vertices.forEach(vertex => {
      vertex.point.clearRenderData();
    });
    graph.edges.forEach(edge => {
      edge.clearRenderData();
    }); // Clear the display

    display.clear();
  }
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  sortElements() {}
  /**
   * Does not need to be implemented by subclass
   */
  // eslint-disable-next-line @typescript-eslint/no-empty-function


  focusPath(path) {}

  isDraggable(point) {
    const draggableTypes = this.transitive.options.draggableTypes;
    if (!draggableTypes) return false;
    let retval = false;
    forEach(draggableTypes, type => {
      if (type === point.getType()) {
        // Return true in ether of the following cases:
        // 1. No ID array is provided for this point type (i.e. entire type is draggable)
        // 2. An ID array is provided and it includes this Point's ID
        retval = !draggableTypes[type] || draggableTypes[type].indexOf(point.getId()) !== -1;
      }
    });
    return retval;
  }

}

/**
 * A Renderer subclass for the default network rendering engine.
 *
 * @param {Object} the main Transitive object
 */

class DefaultRenderer extends Renderer {
  render() {
    super.render();
    const display = this.transitive.display;
    const network = this.transitive.network;
    display.styler = this.transitive.styler; // TODO: restore legend?
    // Process vertices/points

    const focusedPoints = [];
    const unfocusedPoints = [];
    forEach(network.graph.vertices, vertex => {
      vertex.point.clearRenderData();
      if (!vertex.point.isFocused()) unfocusedPoints.push(vertex.point);else focusedPoints.push(vertex.point);
    });
    forEach(network.renderedEdges, rEdge => {
      rEdge.refreshRenderData(display);
    }); // Create arrays of focused, unfocused RenderedSegments

    const focusedSegments = [];
    const unfocusedSegments = [];
    forEach(network.paths, path => {
      forEach(path.segments, pathSegment => {
        forEach(pathSegment.renderedSegments, renderedSegment => {
          if (renderedSegment.isFocused()) focusedSegments.push(renderedSegment);else unfocusedSegments.push(renderedSegment);
        });
      });
    }); // Render elements in the following order:
    // 1. Unfocused segments

    unfocusedSegments.forEach(rs => rs.render(display)); // 2. Unfocused points

    forEach(unfocusedPoints, pt => pt.render(display)); // 3. Focused segments

    focusedSegments.forEach(rs => rs.render(display)); // 4. Focused points

    forEach(focusedPoints, pt => pt.render(display)); // TODO: draw the edge-based points

    /* forEach(network.graph.edges, edge => {
      forEach(edge.pointArray, point => {
        point.render(display)
      })
    }) */
    // 5. Labels

    const labels = this.transitive.labeler.doLayout();
    forEach(labels.pointLabels, label => label.render(display));
    forEach(labels.segmentLabels, label => label.render(display)); // Keep internal collection of rendered segments

    this.renderedSegments = focusedSegments.concat(unfocusedSegments);
  }

  refreshSegmentRenderData() {
    forEach(this.transitive.network.renderedEdges, rEdge => {
      rEdge.refreshRenderData(this.transitive.display);
    }); // try intersecting adjacent rendered edges to create a smooth transition

    const isectKeys = []; // keep track of edge-edge intersections we've already computed

    forEach(this.transitive.network.paths, path => {
      forEach(path.segments, pathSegment => {
        forEach(pathSegment.renderedSegments, rSegment => {
          for (let s = 0; s < rSegment.renderedEdges.length - 1; s++) {
            const rEdge1 = rSegment.renderedEdges[s];
            const rEdge2 = rSegment.renderedEdges[s + 1];
            const key = rEdge1.getId() + '_' + rEdge2.getId();
            if (isectKeys.indexOf(key) !== -1) continue;

            if (rEdge1.graphEdge.isInternal && rEdge2.graphEdge.isInternal) {
              rEdge1.intersect(rEdge2);
            }

            isectKeys.push(key);
          }
        });
      });
    });
  }
  /**
   * sortElements
   */


  sortElements() {
    this.renderedSegments.sort(function (a, b) {
      return a.compareTo(b);
    });
    const focusBaseZIndex = 100000;
    forEach(this.renderedSegments, (rSegment, index) => {
      rSegment.zIndex = index * 10 + (rSegment.isFocused() ? focusBaseZIndex : 0);
    });
    forEach(this.transitive.network.graph.vertices, vertex => {
      const point = vertex.point;
      point.zIndex = point.zIndex + (point.isFocused() ? focusBaseZIndex : 0);
    });
    this.transitive.display.svg.selectAll('.transitive-sortable').sort(function (a, b) {
      const aIndex = typeof a.getZIndex === 'function' ? a.getZIndex() : a.owner.getZIndex();
      const bIndex = typeof b.getZIndex === 'function' ? b.getZIndex() : b.owner.getZIndex();
      return aIndex - bIndex;
    });
  }
  /**
   * focusPath
   */


  focusPath(path) {
    let pathRenderedSegments = [];
    const graph = this.transitive.network.graph;

    if (path) {
      // if we're focusing a specific path
      pathRenderedSegments = path.getRenderedSegments(); // un-focus all internal points

      forEach(graph.edges, edge => {
        edge.pointArray.forEach(function (point, i) {
          point.setAllPatternsFocused(false);
        });
      });
    } else {
      // if we're returning to 'all-focused' mode
      // re-focus all internal points
      forEach(graph.edges, edge => {
        forEach(edge.pointArray, (point, i) => {
          point.setAllPatternsFocused(true);
        });
      });
    } // Keep track of changed segments / points for transition animation (currently disabled)
    const focusedVertexPoints = [];
    forEach(this.renderedSegments, rSegment => {
      if (path && pathRenderedSegments.indexOf(rSegment) === -1) {
        if (rSegment.isFocused()) ;
        rSegment.setFocused(false);
      } else {
        if (!rSegment.isFocused()) ;
        rSegment.setFocused(true);
        focusedVertexPoints.push(rSegment.pathSegment.startVertex().point);
        focusedVertexPoints.push(rSegment.pathSegment.endVertex().point);
      }
    });
    forEach(graph.vertices, vertex => {
      const point = vertex.point;

      if (focusedVertexPoints.indexOf(point) !== -1) {
        if (!point.isFocused()) ;
        point.setFocused(true);
      } else {
        if (point.isFocused()) ;
        point.setFocused(false);
      }
    }); // bring the focused elements to the front for the transition
    // if (path) this.sortElements();
    // TODO: restore transitions

    /*
    // create a transition callback function that invokes refresh() after all transitions complete
    var n = 0
    var refreshOnEnd = (transition, callback) => {
      transition
        .each(() => { ++n })
        .on('end', () => { if (!--n) this.transitive.refresh() })
    }
     // run the transitions on the affected elements
    forEach(focusChangeSegments, segment => {
      segment.runFocusTransition(this.transitive.display, refreshOnEnd)
    })
     forEach(focusChangePoints, point => {
      point.runFocusTransition(this.transitive.display, refreshOnEnd)
    })
    */

    this.transitive.render();
  }

}

/**
 * A Renderer subclass for drawing a simplified representation of the graph
 * itself, i.e. just the edges and vertices.
 *
 * @param {Object} the main Transitive object
 */

class WireframeRenderer extends Renderer {
  render() {
    super.render();
    const {
      display
    } = this.transitive;
    const {
      graph
    } = this.transitive.network; // Draw the edges

    forEach(graph.edges, edge => {
      // Get a basic, non-offset edge renderData array
      const renderData = edge.getRenderCoords(0, 0, display, true);
      display.drawPath(renderData, {
        fill: 'none',
        stroke: '#000',
        'stroke-dasharray': '4,2',
        'stroke-width': 2
      });
    }); // Draw the vertices

    forEach(graph.vertices, vertex => {
      display.drawCircle({
        x: display.xScale.compute(vertex.x),
        y: display.yScale.compute(vertex.y)
      }, {
        fill: '#000',
        r: 4
      });
    });
  }

}

/**
 * Scales for utility functions to use
 */

const zoomScale = /*#__PURE__*/d3.scale.linear().domain([0.25, 1, 4]);
const strokeScale = /*#__PURE__*/d3.scale.linear().domain([0.25, 1, 4]).range([5, 12, 19]);
const fontScale = /*#__PURE__*/d3.scale.linear().domain([0.25, 1, 4]).range([10, 14, 18]);
const notFocusedColor = '#e0e0e0';
/**
 * Expose `utils` for the style functions to use
 */

const utils = {
  defineSegmentCircleMarker: function (display, segment, radius, fillColor) {
    const markerId = 'circleMarker-' + segment.getId();
    display.svg.append('defs').append('svg:marker').attr('id', markerId).attr('refX', radius).attr('refY', radius).attr('markerWidth', radius * 2).attr('markerHeight', radius * 2).attr('markerUnits', 'userSpaceOnUse').append('svg:circle').attr('cx', radius).attr('cy', radius).attr('r', radius).attr('fill', segment.focused ? fillColor : notFocusedColor);
    return 'url(#' + markerId + ')';
  },
  fontSize: function (display) {
    return Math.floor(fontScale(display.scale));
  },
  pixels: function (zoom, min, normal, max) {
    return zoomScale.range([min, normal, max])(zoom);
  },
  strokeWidth: function (display) {
    return strokeScale(display.scale);
  }
};
/**
 * Default Wireframe Edge/Vertex Rules
 */

const wireframeVertices = {
  cx: 0,
  cy: 0,
  fill: '#000',
  r: 3
};
const wireframeEdges = {
  fill: 'none',
  stroke: '#444',
  'stroke-dasharray': '3px 2px',
  'stroke-width': 2
};
/**
 * Default Merged Stops Rules
 */

const stopsMerged = {
  fill: function (display, point, index, utils) {
    return '#fff';
  },

  /**
   *  Transitive-specific attribute specifying any additional padding, in pixels,
   *  to apply to main stop marker. A value of zero (default) results in a that
   *  marker is flush to the edges of the pattern segment(s) the point is set against.
   *  A value greater than zero creates a marker that is larger than the width of
   *  the segments(s).
   */
  'marker-padding': 3,

  /**
   *  Transitive-specific attribute specifying the shape of the main stop marker.
   *  Can be 'roundedrect', 'rectangle' or 'circle'
   */
  'marker-type': ['circle', function (display, data, index, utils) {
    const point = data.owner;
    if ((point.containsBoardPoint() || point.containsAlightPoint()) && !point.containsTransferPoint()) return 'circle';
  }],
  r: function (display, point, index, utils) {
    return utils.pixels(display.scale, 8, 12, 16);
  },
  stroke: function (display, point, index, utils) {
    if (!point.isFocused()) return notFocusedColor;
    return '#000';
  },
  'stroke-width': function (display, point, index, utils) {
    return 2;
  },
  visibility: (display, data) => {
    if (!data.owner.containsSegmentEndPoint()) return 'hidden';
  }
};
/**
 * Stops Along a Pattern
 */

const stopsPattern = {
  cx: 0,
  cy: 0,
  r: [4, (display, data, index, utils) => {
    return utils.pixels(display.scale, 1, 2, 4);
  }, (display, data, index, utils) => {
    const point = data.owner;
    let busOnly = true;
    point.getPatterns().forEach(pattern => {
      if (pattern.route && pattern.route.route_type !== 3) busOnly = false;
    });

    if (busOnly && !point.containsSegmentEndPoint()) {
      return 0.5 * utils.pixels(display.scale, 2, 4, 6.5);
    }
  }],
  stroke: 'none',
  visibility: (display, data) => {
    if (display.scale < 1.5) return 'hidden';
    if (data.owner.containsSegmentEndPoint()) return 'hidden';
  }
};
/**
 * Default place rules
 */

const places = {
  cx: 0,
  cy: 0,
  fill: '#fff',
  r: 14,
  stroke: '#000',
  'stroke-width': '2px'
};
/**
 * Default MultiPoint rules -- based on Stop rules
 */

const multipointsMerged = /*#__PURE__*/Object.assign({}, stopsMerged);
multipointsMerged.visibility = true;
/**
 * Default Multipoint Stops along a pattern
 */

const multipointsPattern = /*#__PURE__*/Object.assign({}, stopsPattern);
/**
 * Default label rules
 */

const labels = {
  'font-family': 'sans-serif',
  'font-size': 15,

  /* 'font-weight': function (display, data, index, utils) {
    var point = data.owner.parent
    if (point.containsBoardPoint() || point.containsAlightPoint()) return 'bold'
  }, */

  /**
   * 'orientations' is a transitive-specific attribute used to specify allowable
   * label placement orientations expressed as one of eight compass directions
   * relative to the point being labeled:
   *
   *        'N'
   *    'NW' |  'NE'
   *       \ | /
   *  'W' -- O -- 'E'
   *       / | \
   *    'SW' | 'SE'
   *        'S
   *
   * Labels oriented 'E' or 'W' are rendered horizontally, 'N' and 'S' vertically,
   * and all others at a 45-degree angle.
   *
   * Returns an array of allowed orientation codes in the order that they will be
   * tried by the labeler.
   */
  orientations: [['E', 'W']]
};
const segmentLabels = {
  background: ['#008', // Background color falls back on dark blue.
  function (display, segment) {
    if (segment.type === 'TRANSIT') {
      if (segment.patterns) {
        if (patternIsDcCirculatorBusRoute(segment.patterns[0])) return '#f00';
        return segment.patterns[0].route.getColor();
      }
    }
  }],
  color: ['#fff', // Text color falls back on white.
  function (display, segment) {
    if (segment.type === 'TRANSIT') {
      if (segment.patterns) {
        if (patternIsDcCirculatorBusRoute(segment.patterns[0])) return '#fff';
        return segment.patterns[0].route.getTextColor();
      }
    }
  }],
  'font-family': 'sans-serif',
  'font-size': 15
};
/**
 * All path segments
 * TODO: update old route-pattern-specific code below
 */

const segments = {
  envelope: [function (display, data, index, utils) {
    const segment = data;

    if (segment.type !== 'TRANSIT') {
      return '8px';
    }

    if (segment.mode === 3) {
      return utils.pixels(display.scale, 4, 6, 10) + 'px';
    }

    return utils.pixels(display.scale, 6, 10, 14) + 'px';
  }],
  stroke: ['#008', // Dark blue
  function (display, segment) {
    if (!segment.focused) return notFocusedColor;

    if (segment.type === 'TRANSIT') {
      if (segment.patterns) {
        if (patternIsDcCirculatorBusRoute(segment.patterns[0])) return '#f00';
        return segment.patterns[0].route.getColor();
      }
    } else if (segment.type === 'CAR') {
      return '#888';
    } else if (segment.type.startsWith('BICYCLE')) {
      return '#f00';
    } else if (segment.type.startsWith('MICROMOBILITY') || segment.type.startsWith('SCOOTER')) {
      return '#f5a729';
    } else if (segment.type === 'WALK') {
      return '#86cdf9';
    }
  }],
  'stroke-dasharray': [false, function (display, data) {
    const segment = data;
    if (segment.type === 'WALK') return '0, 8';

    if (segment.type.startsWith('BICYCLE') || segment.type.startsWith('CAR') || segment.type.startsWith('MICROMOBILITY') || segment.type.startsWith('SCOOTER')) {
      return '8, 3';
    }

    if (segment.frequency && segment.frequency.average < 12) {
      if (segment.frequency.average > 6) return '12, 12';
      return '12, 2';
    }
  }],
  'stroke-linecap': ['butt', (display, segment) => {
    if (segment.type === 'WALK') return 'round';
  }],
  'stroke-width': [10, function (display, segment, index, utils) {
    if (segment.type === 'WALK') return 6;
    if (segment.type.startsWith('BICYCLE')) return 4;
    if (segment.type.startsWith('CAR')) return 4;
    if (segment.type.startsWith('MICROMOBILITY')) return 4;
    if (segment.type.startsWith('SCOOTER')) return 4;
    if (segment.mode === 3) return 6; // Buses
  }]
};
/**
 * Segments Front
 */

const segmentsFront = {
  display: ['none', function (display, data, index, utils) {
    if (patternIsDcCirculatorBusRoute(data.pattern)) {
      return 'inline';
    }
  }],
  fill: 'none',
  stroke: '#008',
  'stroke-width': function (display, data, index, utils) {
    return utils.pixels(display.scale, 3, 6, 10) / 2 + 'px';
  }
};
/**
 * Segments Halo
 */

const segmentsHalo = {
  fill: 'none',
  stroke: '#fff',
  'stroke-linecap': 'round',
  'stroke-width': function (display, data, index, utils) {
    return data.computeLineWidth(display) + 8;
  }
};
/**
 * Label Containers
 */

const segmentLabelContainers = {
  fill: function (display, data) {
    if (!data.isFocused()) return notFocusedColor;
  },
  rx: 3,
  ry: 3,
  'stroke-width': function (display, data) {
    if (patternIsDcCirculatorBusRoute(data.parent.pattern)) return 1;
    return 0;
  }
};
/**
 * Checks that a pattern runs a DC Bus Circulator route. This check dates back
 * to https://github.com/conveyal/transitive.js/commit/b1561dcb6d864fbe6b01de11aa13d06761e1cefd
 * and was likely added to support CarFreeAtoZ (Arlington, VA). However, this may
 * need to be removed because Arlington no longer runs this service. Also, it
 * appears to be hyper-specific to this one implementation.
 */

function patternIsDcCirculatorBusRoute(pattern) {
  if (!pattern) return false;
  const {
    route
  } = pattern;
  const hasShortName = route && route.route_short_name;
  const isBus = route && route.route_type === 3;
  return hasShortName && isBus && route.route_short_name.toLowerCase().substring(0, 2) === 'dc';
}

var styles = {
  labels,
  multipoints_merged: multipointsMerged,
  multipoints_pattern: multipointsPattern,
  places,
  segment_label_containers: segmentLabelContainers,
  segment_labels: segmentLabels,
  segments,
  segments_front: segmentsFront,
  segments_halo: segmentsHalo,
  stops_merged: stopsMerged,
  stops_pattern: stopsPattern,
  utils,
  wireframe_edges: wireframeEdges,
  wireframe_vertices: wireframeVertices
};

/**
 * Element Types
 */

const types = ['labels', 'segments', 'segments_front', 'segments_halo', 'segment_labels', 'segment_label_containers', 'stops_merged', 'stops_pattern', 'places', 'places_icon', 'multipoints_merged', 'multipoints_pattern', 'wireframe_vertices', 'wireframe_edges'];
/**
 * Styler object
 */

class Styler {
  constructor(styles, transitive) {
    // if (!(this instanceof Styler)) return new Styler(styles)
    this.transitive = transitive; // reset styles

    this.reset(); // load styles

    if (styles) this.load(styles);
  }
  /**
   * Clear all current styles
   */


  clear() {
    for (const i in types) {
      this[types[i]] = {};
    }
  }
  /**
   * Reset to the predefined styles
   */


  reset() {
    for (const i in types) {
      const type = types[i];
      this[type] = Object.assign({}, styles[type] || {});

      for (const key in this[type]) {
        if (!Array.isArray(this[type][key])) this[type][key] = [this[type][key]];
      }
    }
  }
  /**
   * Load rules
   *
   * @param {Object} a set of style rules
   */


  load(styles) {
    for (const i in types) {
      const type = types[i];

      if (styles[type]) {
        for (const key in styles[type]) {
          this[type][key] = (this[type][key] || []).concat(styles[type][key]);
        }
      }
    }
  }
  /**
   * Compute a style rule based on the current display and data
   *
   * @param {Array} array of rules
   * @param {Object} the Display object
   * @param {Object} data associated with this object
   * @param {Number} index of this object
   */


  compute(rules, display, data, index) {
    let computed;

    for (const i in rules) {
      const rule = rules[i];
      const val = typeof rule === 'function' ? rule.call(this, display, data, index, styles.utils) : rule;
      if (val !== undefined && val !== null) computed = val;
    }

    return computed;
  }

  compute2(type, attr, data, index) {
    let computed;
    const rules = this[type][attr];
    if (!rules) return null;

    for (const rule of rules) {
      const val = typeof rule === 'function' ? rule.call(this, this.transitive.display, data, index, styles.utils) : rule;
      if (val !== undefined && val !== null) computed = val;
    }

    return computed;
  }
  /**
   * Return the collection of default segment styles for a mode.
   *
   * @param {String} an OTP mode string
   */


  getModeStyles(mode, display) {
    const modeStyles = {}; // simulate a segment w/ the specified style

    const segment = {
      focused: true,
      isFocused: function () {
        return true;
      }
    };

    if (mode === 'WALK' || mode === 'BICYCLE' || mode === 'BICYCLE_RENT' || mode === 'CAR' || mode === 'CAR_RENT' || mode === 'MICROMOBILITY' || mode === 'MICROMOBILITY_RENT' || mode === 'SCOOTER') {
      segment.type = mode;
    } else {
      // assume a transit mode
      segment.type = 'TRANSIT';
      segment.mode = otpModeToGtfsType(mode);
      const route = new Route({
        agency_id: '',
        route_id: '',
        route_long_name: '',
        route_short_name: '',
        route_type: segment.mode
      });
      const pattern = new RoutePattern({});
      route.addPattern(pattern);
      segment.patterns = [pattern];
    }

    for (const attrName in this.segments) {
      const rules = this.segments[attrName];

      for (const i in rules) {
        const rule = rules[i];
        const val = isFunction(rule) ? rule.call(this, display, segment, 0, styles.utils) : rule;

        if (val !== undefined && val !== null) {
          modeStyles[attrName] = val;
        }
      }
    }

    return modeStyles;
  }

}
/**
 * Is function?
 */

function isFunction(val) {
  return Object.prototype.toString.call(val) === '[object Function]';
}

/**
 * SegmentLabel object
 */

class SegmentLabel extends Label {
  constructor(parent, text) {
    super(parent);
    this.labelText = text;
  }

  render(display) {
    const text = this.getText(); // Do not attempt to render label if there is no label text.

    if (!text) return null;
    const x = this.labelAnchor.x - this.containerWidth / 2;
    const y = this.labelAnchor.y - this.containerHeight / 2; // If border-radius is not set, default to +infinity so a disk is rendered.

    const borderRadiusParam = display.styler.compute2('segment_labels', 'border-radius', this.parent) || Infinity;
    const borderRadius = Math.min(borderRadiusParam, this.containerHeight / 2); // Draw rounded rectangle for label.

    display.drawRect({
      x,
      y
    }, {
      // background color
      fill: display.styler.compute2('segment_labels', 'background', this.parent),
      height: this.containerHeight,
      rx: borderRadius,
      ry: borderRadius,
      stroke: display.styler.compute2('segment_labels', 'border-color', this.parent),
      'stroke-width': display.styler.compute2('segment_labels', 'border-width', this.parent),
      width: this.containerWidth
    });
    const fontSize = display.styler.compute2('segment_labels', 'font-size', this.parent); // Offset text location by padding

    display.drawText(text, {
      x: x + this.getPadding(),
      // Offset y by a couple of pixels to account for off-centeredness.
      y: y + this.getPadding() + 2
    }, {
      // text color
      fill: display.styler.compute2('segment_labels', 'color', this.parent),
      'font-family': display.styler.compute2('segment_labels', 'font-family', this.parent),
      // Append 'px' if a unit was not specified in font-size.
      'font-size': getFontSizeWithUnit(fontSize)
    });
  }

  refresh(display) {
    /* if (!this.labelAnchor) return
     if (!this.svgGroup) this.render(display)
     this.svgGroup
      .attr('transform', (d, i) => {
        var tx = (this.labelAnchor.x - this.containerWidth / 2)
        var ty = (this.labelAnchor.y - this.containerHeight / 2)
        return 'translate(' + tx + ',' + ty + ')'
      }) */
  }

  getPadding() {
    return this.textHeight * 0.3;
  }

  computeContainerDimensions() {
    this.containerWidth = this.textWidth + this.getPadding() * 2;
    this.containerHeight = this.textHeight + this.getPadding() * 2;
  }

  getBBox() {
    return {
      height: this.containerHeight,
      width: this.containerWidth,
      x: this.labelAnchor.x - this.containerWidth / 2,
      y: this.labelAnchor.y - this.containerHeight / 2
    };
  }

  intersects(obj) {
    if (obj instanceof Label) {
      // todo: handle label-label intersection for diagonally placed labels separately
      return this.intersectsBBox(obj.getBBox());
    } else if (obj.x && obj.y && obj.width && obj.height) {
      return this.intersectsBBox(obj);
    }

    return false;
  }
  /* clear () {
    this.labelAnchor = null
    if (this.svgGroup) {
      this.svgGroup.remove()
      this.svgGroup = null
    }
  } */


}

/**
 * Labeler object
 */

class Labeler {
  constructor(transitive) {
    this.transitive = transitive;
    this.clear();
  }

  clear() {
    this.points = [];
  }

  updateLabelList(graph) {
    this.points = [];
    forEach(graph.vertices, vertex => {
      const point = vertex.point;

      if (point.getType() === 'PLACE' || point.getType() === 'MULTI' || point.getType() === 'STOP' && point.isSegmentEndPoint) {
        this.points.push(point);
      }
    });
    this.points.sort((a, b) => {
      if (a.containsFromPoint() || a.containsToPoint()) return -1;
      if (b.containsFromPoint() || b.containsToPoint()) return 1;
      return 0;
    });
  }

  updateQuadtree() {
    this.quadtree = d3.geom.quadtree().extent([[-this.width, -this.height], [this.width * 2, this.height * 2]])([]);
    this.addPointsToQuadtree(); // this.addSegmentsToQuadtree();
  }

  addPointsToQuadtree() {
    forEach(this.points, point => {
      const mbbox = point.getMarkerBBox();
      if (mbbox) this.addBBoxToQuadtree(point.getMarkerBBox());
    });
  }

  addSegmentsToQuadtree() {
    forEach(this.transitive.renderSegments, segment => {
      if (segment.getType() !== 'TRANSIT') return;
      let lw = this.transitive.style.compute(this.transitive.style.segments['stroke-width'], this.transitive.display, segment);
      lw = parseFloat(lw.substring(0, lw.length - 2), 10) - 2;
      let x, x1, x2, y, y1, y2; // debug(segment.toString());

      if (segment.renderData.length === 2) {
        // basic straight segment
        if (segment.renderData[0].x === segment.renderData[1].x) {
          // vertical
          x = segment.renderData[0].x - lw / 2;
          y1 = segment.renderData[0].y;
          y2 = segment.renderData[1].y;
          this.addBBoxToQuadtree({
            height: Math.abs(y1 - y2),
            width: lw,
            x: x,
            y: Math.min(y1, y2)
          });
        } else if (segment.renderData[0].y === segment.renderData[1].y) {
          // horizontal
          x1 = segment.renderData[0].x;
          x2 = segment.renderData[1].x;
          y = segment.renderData[0].y - lw / 2;
          this.addBBoxToQuadtree({
            height: lw,
            width: Math.abs(x1 - x2),
            x: Math.min(x1, x2),
            y: y
          });
        }
      }

      if (segment.renderData.length === 4) {
        // basic curved segment
        if (segment.renderData[0].x === segment.renderData[1].x) {
          // vertical first
          x = segment.renderData[0].x - lw / 2;
          y1 = segment.renderData[0].y;
          y2 = segment.renderData[3].y;
          this.addBBoxToQuadtree({
            height: Math.abs(y1 - y2),
            width: lw,
            x: x,
            y: Math.min(y1, y2)
          });
          x1 = segment.renderData[0].x;
          x2 = segment.renderData[3].x;
          y = segment.renderData[3].y - lw / 2;
          this.addBBoxToQuadtree({
            height: lw,
            width: Math.abs(x1 - x2),
            x: Math.min(x1, x2),
            y: y
          });
        } else if (segment.renderData[0].y === segment.renderData[1].y) {
          // horiz first
          x1 = segment.renderData[0].x;
          x2 = segment.renderData[3].x;
          y = segment.renderData[0].y - lw / 2;
          this.addBBoxToQuadtree({
            height: lw,
            width: Math.abs(x1 - x2),
            x: Math.min(x1, x2),
            y: y
          });
          x = segment.renderData[3].x - lw / 2;
          y1 = segment.renderData[0].y;
          y2 = segment.renderData[3].y;
          this.addBBoxToQuadtree({
            height: Math.abs(y1 - y2),
            width: lw,
            x: x,
            y: Math.min(y1, y2)
          });
        }
      }
    });
  }

  addBBoxToQuadtree(bbox) {
    if (bbox.x + bbox.width / 2 < 0 || bbox.x - bbox.width / 2 > this.width || bbox.y + bbox.height / 2 < 0 || bbox.y - bbox.height / 2 > this.height) {
      return;
    }

    this.quadtree.add([bbox.x + bbox.width / 2, bbox.y + bbox.height / 2, bbox]);
    this.maxBBoxWidth = Math.max(this.maxBBoxWidth, bbox.width);
    this.maxBBoxHeight = Math.max(this.maxBBoxHeight, bbox.height);
  }

  doLayout() {
    this.width = this.transitive.display.width;
    this.height = this.transitive.display.height;
    this.maxBBoxWidth = 0;
    this.maxBBoxHeight = 0;
    this.updateQuadtree();
    return {
      pointLabels: this.placePointLabels(),
      segmentLabels: this.placeSegmentLabels()
    };
  }
  /** placePointLabels **/


  placePointLabels() {
    const styler = this.transitive.styler;
    const placedLabels = []; // var labeledPoints = []

    forEach(this.points, point => {
      const labelText = point.label.getText();
      if (!labelText) return;
      point.label.fontFamily = styler.compute2('labels', 'font-family', point);
      point.label.fontSize = styler.compute2('labels', 'font-size', point);
      const textDimensions = measureText({
        fontFamily: point.label.fontFamily || 'sans-serif',
        fontSize: point.label.fontSize,
        lineHeight: 1.2,
        text: labelText
      });
      point.label.textWidth = textDimensions.width.value;
      point.label.textHeight = textDimensions.height.value;
      const orientations = styler.compute(styler.labels.orientations, this.transitive.display, {
        point: point
      });
      let placedLabel = false;

      for (let i = 0; i < orientations.length; i++) {
        point.label.setOrientation(orientations[i]);
        if (!point.focused) continue;
        if (!point.label.labelAnchor) continue;
        const lx = point.label.labelAnchor.x;
        const ly = point.label.labelAnchor.y; // do not place label if out of range

        if (lx <= 0 || ly <= 0 || lx >= this.width || ly > this.height) continue;
        const labelBBox = point.label.getBBox();
        const overlaps = this.findOverlaps(point.label, labelBBox); // do not place label if it overlaps with others

        if (overlaps.length > 0) continue; // if we reach this point, the label is good to place

        point.label.setVisibility(true); // labeledPoints.push(point)

        placedLabels.push(point.label);
        this.quadtree.add([labelBBox.x + labelBBox.width / 2, labelBBox.y + labelBBox.height / 2, point.label]);
        this.maxBBoxWidth = Math.max(this.maxBBoxWidth, labelBBox.width);
        this.maxBBoxHeight = Math.max(this.maxBBoxHeight, labelBBox.height);
        placedLabel = true;
        break; // do not consider any other orientations after places
      } // end of orientation loop
      // if label not placed at all, hide the element


      if (!placedLabel) {
        point.label.setVisibility(false);
      }
    });
    return placedLabels;
  }
  /** placeSegmentLabels **/


  placeSegmentLabels() {
    this.placedLabelKeys = [];
    const placedLabels = []; // collect the bus RenderSegments
    forEach(this.transitive.network.paths, path => {
      forEach(path.getRenderedSegments(), rSegment => {
      });
    }); // Generate labels for GTFS route/mode types listed in labeledModes if provided.
    // If labeled modes is not provided, only label the buses (mode '3').

    let edgeGroups = [];
    const busModes = [3];
    const labeledModes = this.transitive.options.labeledModes || busModes;
    forEach(this.transitive.network.paths, path => {
      forEach(path.segments, segment => {
        if (segment.type === 'TRANSIT' && labeledModes.includes(segment.getMode())) {
          edgeGroups = edgeGroups.concat(segment.getLabelEdgeGroups());
        }
      });
    }); // iterate through the sequence collection, labeling as necessary

    forEach(edgeGroups, edgeGroup => {
      this.currentGroup = edgeGroup; // get the array of label strings to be places (typically the unique
      // route short names)

      this.labelTextArray = edgeGroup.getLabelTextArray(); // create the initial label for placement

      this.labelTextIndex = 0;
      let label = this.getNextLabel();
      if (!label) return; // Iterate through potential anchor locations, attempting placement at
      // each one

      const labelAnchors = edgeGroup.getLabelAnchors(this.transitive.display, label.textHeight * 1.5);

      for (let i = 0; i < labelAnchors.length; i++) {
        label.labelAnchor = labelAnchors[i];
        const {
          x,
          y
        } = label.labelAnchor; // do not consider this anchor if it is out of the display range

        if (!this.transitive.display.isInRange(x, y)) continue; // check for conflicts with existing placed elements

        const conflicts = this.findOverlaps(label, label.getBBox());

        if (conflicts.length === 0) {
          // If no overlaps/conflicts encountered, place the current label.
          placedLabels.push(label); // Track new label in quadtree.

          this.quadtree.add([x, y, label]);
          label = this.getNextLabel();
          if (!label) break;
        }
      } // end of anchor iteration loop

    }); // end of sequence iteration loop

    return placedLabels;
  }

  getNextLabel() {
    while (this.labelTextIndex < this.labelTextArray.length) {
      const labelText = this.labelTextArray[this.labelTextIndex];
      const key = this.currentGroup.edgeIds + '_' + labelText;

      if (this.placedLabelKeys.indexOf(key) !== -1) {
        this.labelTextIndex++;
        continue;
      }

      const label = this.constructSegmentLabel(this.currentGroup.renderedSegment, labelText);
      this.placedLabelKeys.push(key);
      this.labelTextIndex++;
      return label;
    }

    return null;
  }

  constructSegmentLabel(segment, labelText) {
    const label = new SegmentLabel(segment, labelText);
    const styler = this.transitive.styler;
    label.fontFamily = styler.compute2('segment_labels', 'font-family', segment);
    label.fontSize = styler.compute2('segment_labels', 'font-size', segment);
    const textDimensions = measureText({
      fontFamily: label.fontFamily || 'sans-serif',
      // Append 'px' if a unit was not specified in font-size.
      fontSize: getFontSizeWithUnit(label.fontSize),
      lineHeight: 1.2,
      text: labelText
    });
    label.textWidth = textDimensions.width.value;
    label.textHeight = textDimensions.height.value;
    label.computeContainerDimensions();
    return label;
  }

  findOverlaps(label, labelBBox) {
    // Get bounding box to check.
    const minX = labelBBox.x - this.maxBBoxWidth / 2;
    const minY = labelBBox.y - this.maxBBoxHeight / 2;
    const maxX = labelBBox.x + labelBBox.width + this.maxBBoxWidth / 2;
    const maxY = labelBBox.y + labelBBox.height + this.maxBBoxHeight / 2; // debug('findOverlaps %s,%s %s,%s', minX,minY,maxX,maxY);

    const matchItems = []; // Check quadtree for potential collisions.

    this.quadtree.visit((node, x1, y1, x2, y2) => {
      const {
        point
      } = node;

      if (point) {
        const [pX, pY, pLabel] = point;

        if (pX >= minX && pX < maxX && pY >= minY && pY < maxY && label.intersects(pLabel)) {
          matchItems.push(pLabel);
        }
      } // No need to visit children of this node if bbox falls entirely within
      // this node.


      return x1 > maxX || y1 > maxY || x2 < minX || y2 < minY;
    });
    return matchItems;
  }

  findNearbySegmentLabels(label, x, y, buffer) {
    const minX = x - buffer;
    const minY = y - buffer;
    const maxX = x + buffer;
    const maxY = y + buffer; // debug('findNearby %s,%s %s,%s', minX,minY,maxX,maxY);

    const matchItems = [];
    this.quadtree.visit((node, x1, y1, x2, y2) => {
      const p = node.point;

      if (p && p[0] >= minX && p[0] < maxX && p[1] >= minY && p[1] < maxY && p[2].parent && label.parent.patternIds === p[2].parent.patternIds) {
        matchItems.push(p[2]);
      }

      return x1 > maxX || y1 > maxY || x2 < minX || y2 < minY;
    });
    return matchItems;
  }

}

/* eslint-disable camelcase */
class Transitive {
  constructor(options) {
    if (!(this instanceof Transitive)) return new Transitive(options);
    this.options = options;
    if (this.options.zoomEnabled === undefined) this.options.zoomEnabled = true;
    if (this.options.autoResize === undefined) this.options.autoResize = true;
    if (this.options.groupEdges === undefined) this.options.groupEdges = true;
    if (options.el) this.el = options.el;
    this.display = this.options.display === 'canvas' ? new CanvasDisplay(this) : new SvgDisplay(this);
    this.data = options.data;
    this.setRenderer(this.options.initialRenderer || 'default');
    this.labeler = new Labeler(this);
    this.styler = new Styler(options.styles, this);

    if (options.initialBounds) {
      this.display.fitToWorldBounds([sm.forward(options.initialBounds[0]), sm.forward(options.initialBounds[1])]);
    }
  }
  /**
   * Clear the Network data and redraw the (empty) map
   */


  clearData() {
    this.network = this.data = null;
    this.labeler.clear(); // @ts-expect-error See notes in constructor about emit method.

    this.emit('clear data', this);
  }
  /**
   * Update the Network data and redraw the map
   */


  updateData(data, resetDisplay) {
    this.network = null;
    this.data = data;
    if (resetDisplay) this.display.reset();else if (this.data) this.display.scaleSet = false;
    this.labeler.clear(); // @ts-expect-error See notes in constructor about emit method.

    this.emit('update data', this);
  }
  /**
   * Return the collection of default segment styles for a mode.
   *
   * @param {String} an OTP mode string
   */


  getModeStyles(mode) {
    return this.styler.getModeStyles(mode, this.display || new Display(this));
  }
  /** Display/Render Methods **/

  /**
   * Set the DOM element that serves as the main map canvas
   */


  setElement(el) {
    if (this.el) d3.select(this.el).selectAll('*').remove();
    this.el = el; // @ts-expect-error See notes in constructor about emit method.

    this.emit('set element', this, this.el);
    return this;
  }
  /**
   * Set the DOM element that serves as the main map canvas
   */


  setRenderer(type) {
    switch (type) {
      case 'wireframe':
        this.renderer = new WireframeRenderer(this);
        break;

      case 'default':
        this.renderer = new DefaultRenderer(this);
        break;
    }
  }
  /**
   * Render
   */


  render() {
    if (!this.network) {
      this.network = new Network(this, this.data);
    }

    if (!this.display.scaleSet) {
      this.display.fitToWorldBounds(this.network.graph.bounds());
    }

    this.renderer.render(); // @ts-expect-error See notes in constructor about emit method.

    this.emit('render', this);
  }
  /**
   * Render to
   *
   * @param {Element} el
   */


  renderTo(el) {
    this.setElement(el);
    this.render(); // @ts-expect-error See notes in constructor about emit method.

    this.emit('render to', this);
    return this;
  }
  /**
   * focusJourney
   */


  focusJourney(journeyId) {
    if (!this.network) {
      console.warn('Transitive network is not defined! Cannot focus journey!');
      return;
    }

    const journey = this.network.journeys[journeyId];
    const path = (journey == null ? void 0 : journey.path) || null;
    this.renderer.focusPath(path);
  }
  /**
   * Sets the Display bounds
   * @param {Array} lon/lat bounds expressed as [[west, south], [east, north]]
   */


  setDisplayBounds(llBounds) {
    if (!this.display) return;
    const smWestSouth = sm.forward(llBounds[0]);
    const smEastNorth = sm.forward(llBounds[1]); // reset the display to make sure the correct display scale is recomputed
    // see https://github.com/conveyal/transitive.js/pull/50
    // FIXME this is a work-around for some other problem that occurs somewhere
    //   else. To investigate further, uncomment this line and compare the
    //   differences near the Putnam Plaza place in the Putnam Bug story.

    this.display.reset();
    this.display.setXDomain([smWestSouth[0], smEastNorth[0]]);
    this.display.setYDomain([smWestSouth[1], smEastNorth[1]]);
    this.display.computeScale();
  }
  /**
   * Gets the Network bounds
   * @returns {Array} lon/lat bounds expressed as [[west, south], [east, north]]
   */


  getNetworkBounds() {
    if (!this.network || !this.network.graph) return null;
    const graphBounds = this.network.graph.bounds();
    const ll1 = sm.inverse(graphBounds[0]);
    const ll2 = sm.inverse(graphBounds[1]);
    return [[Math.min(ll1[0], ll2[0]), Math.min(ll1[1], ll2[1])], [Math.max(ll1[0], ll2[0]), Math.max(ll1[1], ll2[1])]];
  }
  /**
   * resize
   */


  resize(width, height) {
    if (!this.display) return; // @ts-expect-error I have no idea what magic this display object uses to
    // get an el property. - Evan :(

    d3.select(this.display.el).style('width', width + 'px').style('height', height + 'px'); // @ts-expect-error I have no idea what magic this display object uses to
    // get a resized method. - Evan :(

    this.display.resized();
  }
  /**
   * trigger a display resize action (for externally-managed SVG containers)
   */


  resized(width, height) {
    // @ts-expect-error I have no idea what magic this display object uses to
    // get a resized method. - Evan :(
    this.display.resized(width, height);
  }

  setTransform(transform) {
    this.display.applyTransform(transform);
    this.render();
  }
  /** editor functions **/


  createVertex(wx, wy) {
    if (!this.network) {
      console.warn('Transitive network is not defined! Cannot create vertex!');
      return;
    }

    this.network.graph.addVertex(new Point(), wx, wy);
  }

}
/**
 * Mixin `Emitter`
 */

Emitter(Transitive.prototype);

export default Transitive;
//# sourceMappingURL=transitive-js.esm.js.map
